<script setup>
    import router from '../../router/routes';
    import { ref, reactive, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
  
    import { useRoute } from 'vue-router'
    const route = useRoute();

    import Reminder from '@/components/Reminder.vue'
    import Spinner from '@/components/Spinner.vue'
    import InlineSpinner from '@/components/InlineSpinner.vue';
    import userAvatar from '@/components/userAvatar.vue'
    import CustomerDetails from '@/components/businessPartner/CustomerDetails.vue'
    import PreviewHeader from '@/components/PreviewHeader.vue'
    import SalesPitchStatus from '@/components/businessPartner/SalesPitchStatus.vue'
    import PotentialSection from '@/components/businessPartner/PotentialSection.vue'
    import { defineAsyncComponent } from 'vue';
    const Products360 = defineAsyncComponent({
        loader: () => import('@/components/businessPartner/Products360.vue'),
        loadingComponent: Spinner,
        delay: 200,
    });
    const CasesTicket = defineAsyncComponent({
        loader: () => import('@/components/businessPartner/CasesTicket.vue'),
        loadingComponent: Spinner,
        delay: 200,
    });
    // TEMPORÄR DEAKTIVIERT: Feature "Verwandte Kunden" nutzt noch Mock-Daten im Backend, siehe RelatedBusinessPartners.vue
    // const RelatedBusinessPartners = defineAsyncComponent({
    //     loader: () => import('@/components/businessPartner/RelatedBusinessPartners.vue'),
    //     loadingComponent: Spinner,
    //     delay: 200,
    // });
    import SalesPitchHistory from '@/components/businessPartner/SalesPitchHistory.vue'
    import SalesMatrix from '@/components/businessPartner/SalesMatrix.vue'
    import AddressInfo from '@/components/businessPartner/AddressInfo.vue'
    import DecisionMaker from '@/components/businessPartner/DecisionMaker.vue'
    import folderOpenIcon from '@/assets/imgs/icons/folder-open-solid-full.svg'
    import folderClosedIcon from '@/assets/imgs/icons/folder-solid-full.svg'
    import { SALES_PITCH_STATUS_IDS } from '@/constants/status'

    import {emailIsValid} from '@/stores/helpers/emailIsValid'

    import { useBusinessPartnersStore } from '@/stores/businessPartners'
    const businessPartnersStore = useBusinessPartnersStore();

    import { useLeadGenerationStore } from '@/stores/leadGeneration'
    const leadGenerationStore = useLeadGenerationStore();

    import { useUserStore } from '@/stores/user'
    const userStore = useUserStore();

    // Lokaler Zustand: Overlay beim Zurück-Navigieren zur Liste anzeigen
    const isNavigatingBack = ref(false);
    const isSwitchingBusinessPartner = ref(false);
    const isOpeningNextCustomer = ref(false);
    const salesPitchStatusRenderKey = ref(0);
    let previewHeaderBackResetTimer = null;
        // console.log('P360LOG INIT: BusinessPartner.vue geladen');
        onMounted(() => {
            // console.log('P360LOG onMounted businessPartner', JSON.parse(JSON.stringify(businessPartnersStore.businessPartner)));
            // console.log('P360LOG onMounted contractList', businessPartnersStore.businessPartner?.contractList);
        });
        watch(() => businessPartnersStore.businessPartner, (val) => {
            // console.log('P360LOG WATCH businessPartner', JSON.parse(JSON.stringify(val)));
            // console.log('P360LOG WATCH contractList', val?.contractList);
        }, { deep: true });

    // Alias für vorhandene Helper-Funktion (im Code weiter unten wird isValidEmail verwendet)
    const isValidEmail = emailIsValid;

    // Reaktives Status-Objekt gemäß Originaldefinition mit korrekten IDs
    const status = reactive({
        currentStep: 1, // aktueller Schritt
        allowPrevious: false,
        allowNext: false,
        history: [],
        'Entscheider Kontakt hergestellt': {
            display: true,
            options: {
                'Ja, telefonisch': { id: 3, display: true, checked: false, isParent: false },
                'Ja, vor Ort': { id: 4, display: true, checked: false, isParent: false },
                'Nein': { id: 2, display: true, checked: false, isParent: false },
                'Ungültig: Dublette': { id: 9, display: true, checked: false },
                'Ungültig: kein Anschluss': { id: 8, display: true, checked: false },
            }
        },
        'Beendet':  {
            display: false,
            options: {
                'Geschäftsaufgabe': { id: 26, display: true, checked: false },
                'Entscheider nie erreicht': { id: 27, display: true, checked: false },
                'Entscheider desinteressiert': { id: 22, display: true, checked: false },
            }
        },
        'Verbindlicher Termin mit Entscheider': {
            display: false,
            options: {
                'Rückruf': { id: 28, display: true, checked: false },
                'Termin vor Ort': { id: 24, display: true, checked: false }
            }
        },
        'Unverbindliche Wiedervorlage': {
            display: false,
            options: {
                'Datum eintragen': { id: 31, display: true, checked: false }
            }
        },
        datePicker: {
            unverbindlich: { title: '', inputTime: 'optional', addendum: '', selected: { date: null, time: null } },
            verbindlich: { title: '', inputTime: 'required', addendum: '', selected: { date: null, time: null } }
        }
    });

    /* -------------------------------------------------------------
     * Reporting Table – Revenue Heatmap & KPI helpers
     * -------------------------------------------------------------
     * We normalise directory specific revenues (oe,tb,gs,omp) to the
     * maximum value > 0 to derive: (1) a visual heat bucket class
     * (2) proportional KPI bar width (ratio 0–1).
     * Total is kept for display, not part of max normalisation logic.
     * All helpers are side-effect free and defensive against
     * undefined / non–numeric backend responses.
     */

    /* -------------------------------------------------------------
     * DSM Debug Utilities
     * Toggle DSM_DEBUG to enable/disable verbose console tracing.
     * ------------------------------------------------------------- */
    const DSM_DEBUG = false; // set to true to enable verbose console tracing
    function dsmLog(...args){ if(DSM_DEBUG) console.log('[DSM]', ...args); }

    const revenueDirKeys = ['oe','tb','gs','omp'];

    // Normalisiere Quelle: Backend liefert teils salesValues (plural) oder salesValue (singular) & gemischte Groß/Kleinschreibung (OMP/OMP etc.)
    const rawSalesValue = computed(()=> {
        const bp = businessPartnersStore.businessPartner || {};
        const source = bp.salesValue || bp.salesValues || {};
        const out = {};
        for(const key of Object.keys(source)){
            const lower = key.toLowerCase();
            if(['total','omp','oe','tb','gs'].includes(lower)){
                out[lower] = source[key];
            }
        }
        if(DSM_DEBUG) dsmLog('mapped salesValue(s) source keys', Object.keys(source), '-> normalized', out);
        return out;
    });
    watch(rawSalesValue, (nv, ov) => { dsmLog('rawSalesValue changed', { previous: ov, next: nv }); }, { deep:true });

    function toNumber(v){
        if(v === null || v === undefined){ if(DSM_DEBUG) dsmLog('toNumber null/undefined -> 0'); return 0; }
        if(typeof v === 'number') { const out = Number.isFinite(v) ? v : 0; if(DSM_DEBUG) dsmLog('toNumber number in', v, 'out', out); return out; }
        if(typeof v === 'string'){
            let original = v;
            let s = v.trim();
            if(!s){ if(DSM_DEBUG) dsmLog('toNumber empty string -> 0'); return 0; }
            // Entferne Währungs-Symbole, Leerzeichen und alle nicht zulässigen Zeichen außer Ziffern, . , -
            s = s.replace(/€/g,'').replace(/\s+/g,'');
            // Falls s wie "1.234,56" (de-DE) aufgebaut ist
            const germanPattern = /^-?\d{1,3}(\.\d{3})*(,\d+)?$/;
            if(germanPattern.test(s)){
                const before = s;
                s = s.replace(/\./g,'').replace(',', '.');
                if(DSM_DEBUG) dsmLog('toNumber germanPattern matched', original, 'cleaned', before, '->', s);
            } else if(/^-?\d+,\d+$/.test(s)) { // einfache Variante: "123,45"
                const before = s; s = s.replace(',', '.'); if(DSM_DEBUG) dsmLog('toNumber simple comma decimal', before, '->', s);
            } else {
                // Entferne alle Zeichen außer Ziffern . -
                const before = s;
                s = s.replace(/[^0-9+\-.]/g,'');
                if(DSM_DEBUG && before !== s) dsmLog('toNumber stripped non-numeric', before, '->', s);
                // Wenn beide Trennzeichen vorhanden (z.B. 1,234.56) -> Entferne Kommata als Tausender
                if(/\d[,.]\d{3}[,.]/.test(s)){
                    const b2 = s; s = s.replace(/,/g,''); if(DSM_DEBUG) dsmLog('toNumber removed thousand commas', b2, '->', s);
                }
            }
            const n = Number(s);
            if(DSM_DEBUG) dsmLog('toNumber parsed', s, '->', n);
            return Number.isFinite(n) ? n : 0;
        }
        if(DSM_DEBUG) dsmLog('toNumber unsupported type', typeof v, 'value', v, '-> 0');
        return 0;
    }

    // Normalize product ids coming from filters/dropdowns (e.g., "product:123" -> 123)
    function normalizeProductId(v){
        if (v === null || v === undefined) return -1;
        if (typeof v === 'number') return v;
        const s = String(v);
        const m = s.match(/(\d+)$/);
        return m ? parseInt(m[1], 10) : -1;
    }

    const revenueValues = computed(()=> revenueDirKeys.map(k => toNumber(rawSalesValue.value?.[k])));
    watch(revenueValues, (nv)=> { dsmLog('revenueValues recomputed', nv); });

    const maxRevenue = computed(()=> {
        const positives = revenueValues.value.filter(v=> v>0);
        const max = positives.length ? Math.max(...positives) : 0;
        if(DSM_DEBUG) dsmLog('maxRevenue computed', max, 'from', positives);
        return max;
    });
    watch(maxRevenue, (nv, ov)=> { dsmLog('maxRevenue changed', ov, '->', nv); });

    // Aggregated total if backend missing or inconsistent
    const computedRevenueTotal = computed(()=>{
        const backend = toNumber(rawSalesValue.value?.total);
        const parts = ['oe','tb','gs','omp'].map(k=> toNumber(rawSalesValue.value?.[k]));
        const sum = parts.reduce((a,b)=> a + (Number.isFinite(b)?b:0),0);
        if(DSM_DEBUG) dsmLog('computedRevenueTotal parts', parts, 'sum', sum, 'backend', backend);
        // Wenn Backend plausibel und Summe 0 (Teilwerte evtl. nicht geliefert) -> Backend verwenden
        if(backend > 0 && sum === 0) return backend;
        // Wenn beide > 0 und innerhalb 1% -> Backend bevorzugen (verhindert Rundungsdifferenzen)
        if(backend > 0 && sum > 0 && Math.abs(backend - sum)/sum < 0.01) return backend;
        // Sonst Summe nehmen (auch wenn Backend 0 oder inkonsistent)
        return sum;
    });
    watch(computedRevenueTotal, (nv, ov)=> { dsmLog('computedRevenueTotal changed', ov, '->', nv); });

    function revenueAmount(key){
        const amt = toNumber(rawSalesValue.value?.[key]);
        if(DSM_DEBUG) dsmLog('revenueAmount', key, '->', amt);
        return amt;
    }

    function revenueRatio(key){
        const max = maxRevenue.value;
        if(!max){ if(DSM_DEBUG) dsmLog('revenueRatio', key, 'no max -> 0'); return 0; }
        const ratio = +(revenueAmount(key) / max).toFixed(4);
        if(DSM_DEBUG) dsmLog('revenueRatio', key, '->', ratio, '(max', max, ')');
        return ratio;
    }

    // Heatmap entfernt – Bucket Funktion nicht mehr benötigt

    function formatRevenue(value){
        const n = toNumber(value);
        if(n === 0) return '0';
        try {
            return n.toLocaleString('de-DE');
        } catch(e){
            return String(n);
        }
    }

    // Tooltip helper (basic – can be extended later)
    function revenueTooltip(key){
        const amount = revenueAmount(key);
        if(!amount) return 'Kein Umsatz';
        return `Umsatz: ${formatRevenue(amount)} €`;
    }

    // Expose helpers for template usage
    const reportingHelpers = {
        revenueDirKeys,
        maxRevenue,
        revenueAmount,
        revenueRatio,
        formatRevenue,
        revenueTooltip,
        computedRevenueTotal
    };

    // Robust opener for reporting links (fallback if target=_blank blocked)
    function openReporting(context){
        const base = businessPartnersStore.businessPartner?.reportingLink;
        if(!base) return;
        const url = context ? `${base}&context=${context}` : base;
        try {
            const w = window.open(url, '_blank', 'noopener,noreferrer');
            if(!w){
                // Popup blocked – fallback same tab
                window.location.href = url;
            }
        } catch(e){
            window.location.href = url;
        }
    }

    /* -------------------------------------------------------------
     * Products 360° helpers (wie in BusinessPartner_works.vue)
     * ------------------------------------------------------------- */
        // Track which product (by name) is expanded inside a productGroup+status context
        const openProducts = ref(new Set());

        // Fallback: Produkte aus contractList generieren, falls products leer ist
        function buildProductsFromContracts(contractList) {
            if (!Array.isArray(contractList)) return [];
            const map = new Map();
            for (const contract of contractList) {
                const product = contract.product || {};
                const groupName = product.isOMP ? 'OMP' : (contract.publication ? contract.publication.split(' ')[0] : '');
                const signature = `${product.id}:${groupName}`;
                if (!map.has(signature)) {
                    map.set(signature, {
                        id: product.id,
                        signature,
                        name: product.name,
                        listAs: product.listAs,
                        sortOrder: product.sortOrder,
                        productGroup: { name: groupName, sortOrder: 0, reportingLink: null },
                        contracts: []
                    });
                }
                map.get(signature).contracts.push(contract);
            }
            // Status setzen wie im Store
            const products = Array.from(map.values());
            products.forEach(product => {
                product.contracts.sort((a, b) => {
                    const aEnd = parseInt(a.dates?.end || 0);
                    const bEnd = parseInt(b.dates?.end || 0);
                    return bEnd - aEnd;
                });
                if (product.contracts[0]?.dates?.end < (userStore.currentSelection.campaign?.dateStart || 0)) {
                    product.status = 'history';
                } else if (['green'].includes(product.contracts[0]?.status?.signal?.trim())) {
                    product.status = 'active';
                } else {
                    product.status = 'terminated';
                }
            });
            products.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
            return products;
        }

        function getProducts() {
            const bp = businessPartnersStore.businessPartner;
            if (!bp) return [];
            if (Array.isArray(bp.products) && bp.products.length > 0) return bp.products;
            if (Array.isArray(bp.contractList) && bp.contractList.length > 0) {
                const fallback = buildProductsFromContracts(bp.contractList);
                // console.log('P360LOG Fallback products from contractList', fallback);
                return fallback;
            }
            return [];
        }
    function productKey(status, group, name){
        const key = `${status}__${group}__${name}`;
        // console.log('P360LOG productKey', { status, group, name, key });
        return key;
    }
    function toggleProduct(status, group, name){
        const key = productKey(status, group, name);
        const wasOpen = openProducts.value.has(key);
        if(wasOpen) openProducts.value.delete(key); else openProducts.value.add(key);
        // console.log('P360LOG toggleProduct', { status, group, name, key, wasOpen, openProducts: Array.from(openProducts.value) });
    }
    function isProductOpen(status, group, name){
        const open = openProducts.value.has(productKey(status, group, name));
        // console.log('P360LOG isProductOpen', { status, group, name, open });
        return open;
    }
    // Legacy status kept for backward compatibility, new grouping by contract signals
    function productsByStatus(status){
        const arr = getProducts().filter(p=> p.status===status);
        // console.log('P360LOG productsByStatus', { status, arr });
        return arr;
    }
    function productGroups(){
        const arr = ['OMP','GS','TB','OE'];
        // console.log('P360LOG productGroups', arr);
        return arr;
    }
    function productsByStatusAndGroup(status, group){
        const arr = productsByStatus(status).filter(p=> p.productGroup?.name===group);
        // console.log('P360LOG productsByStatusAndGroup', { status, group, arr });
        return arr;
    }
    function groupsForStatus(status){
        const arr = productGroups().filter(g => productsByStatusAndGroup(status, g).length);
        // console.log('P360LOG groupsForStatus', { status, arr });
        return arr;
    }

    // Contract Signal Grouping (green / yellow / red)
    const SIGNAL_ORDER = ['green','yellow','red'];
    function normaliseSignal(contract){
        const sig = contract?.status?.signal || contract?.signal || 'green';
        // console.log('P360LOG normaliseSignal', { contract, sig });
        return sig;
    }
    function formatGermanDate(value){
        if(!value) return '';
        try {
            const iso = value.replace(' ', 'T');
            const date = new Date(iso).toLocaleDateString('de-DE');
            // console.log('P360LOG formatGermanDate', { value, date });
            return date;
        } catch(e){
            // console.log('P360LOG formatGermanDate ERROR', { value, e });
            return value;
        }
    }
    function groupedContractsForProduct(product){
        const groups = { green: [], yellow: [], red: [] };
        for(const c of (product?.contracts||[])){
            const sig = normaliseSignal(c);
            if(!groups[sig]) groups[sig] = []; // defensive
            groups[sig].push(c);
        }
        // console.log('P360LOG groupedContractsForProduct', { product, groups });
        return groups;
    }
    function hasAnySignalContracts(product){
        const g = groupedContractsForProduct(product);
        const any = SIGNAL_ORDER.some(s => g[s]?.length);
        // console.log('P360LOG hasAnySignalContracts', { product, any });
        return any;
    }
    function allProducts(){
        const arr = getProducts();
        // console.log('P360LOG allProducts', arr);
        return arr;
    }
    function productsAllByGroup(group){
        const arr = allProducts().filter(p => p.productGroup?.name === group);
        // console.log('P360LOG productsAllByGroup', { group, arr });
        return arr;
    }
    function groupsWithAnyProducts(){
        const arr = productGroups().filter(g => productsAllByGroup(g).length);
        // console.log('P360LOG groupsWithAnyProducts', arr);
        return arr;
    }
    function productsForSignalAndGroup(signal, group){
        // A product is shown under a signal if at least one of its contracts has that signal
        const arr = productsAllByGroup(group).filter(p => groupedContractsForProduct(p)[signal]?.length);
        // console.log('P360LOG productsForSignalAndGroup', { signal, group, arr });
        return arr;
    }
    function anyProductsForSignal(signal){
        const any = groupsWithAnyProducts().some(g => productsForSignalAndGroup(signal, g).length);
        // console.log('P360LOG anyProductsForSignal', { signal, any });
        return any;
    }
    // Contract-Dokumente Handling wie gehabt
    const openDocs = ref(new Set());
    function isContractDocsOpen(id){
        const open = openDocs.value.has(String(id));
        // console.log('P360LOG isContractDocsOpen', { id, open });
        return open;
    }
    function toggleContractDocs(id){
        const k = String(id);
        const wasOpen = openDocs.value.has(k);
        if (wasOpen) openDocs.value.delete(k); else openDocs.value.add(k);
        // console.log('P360LOG toggleContractDocs', { id, wasOpen, openDocs: Array.from(openDocs.value) });
    }
    function getDocumentsForContract(contract){
        try {
            const orderNo = contract?.orderNo;
            const list = businessPartnersStore.businessPartner?.contractDocuments || [];
            const entry = list.find(d => String(d.orderNo) === String(orderNo));
            const docs = Array.isArray(entry?.orderDocuments) ? entry.orderDocuments : [];
            return docs;
        } catch(e){
            return [];
        }
    }
    function formatDocDate(d){
        const date = formatGermanDate(d);
        // console.log('P360LOG formatDocDate', { d, date });
        return date;
    }

    async function handleDocumentClick(doc) {
        // Neuer Endpoint / Payload: /businessPartners/v1/getBusinessPartnerContractFile
        // Fix: bisheriger relativer fetch traf den Vite Dev Server (Port 5137) -> 404.
        // Jetzt Nutzung des gleichen Base-URLs + Auth wie alle anderen Requests.
        try {
            const sugarId = doc?.id || doc?.sugar_id || doc?.sugarId || doc?.document_id || doc?.documentId;
            if(!sugarId) throw new Error('Kein Dokument Sugar-ID gefunden');
            const userId = userStore?.id;
            if(!userId) throw new Error('Keine userStore.id verfügbar für Request Payload');
            const payload = { id: userId, document: { sugar: { id: sugarId } } };

            // Auth Token aus AuthStore holen (wie fetchFromBackend) ohne doppelten Code kopieren
            // Um kein großes Refactoring zu machen, lazy import des Helpers
            const { fetchFromBackend } = await import('@/stores/helpers/backend.mjs');
            const data = await fetchFromBackend('businessPartners/v1/getBusinessPartnerContractFile', payload);

            let base64 = data?.pdf || data?.PDF || data?.file || null;
            if(typeof data === 'string' && /^JVBER/i.test(data)) base64 = data; // direkter PDF String
            if(!base64){
                for(const k of Object.keys(data||{})){
                    if(typeof data[k] === 'string' && /^JVBER/i.test(data[k])) { base64 = data[k]; break; }
                }
            }
            if(!base64) throw new Error('Kein Base64 PDF im Response');

            const byteChars = atob(base64.replace(/^data:application\/pdf;base64,/i,''));
            const byteNumbers = new Array(byteChars.length);
            for (let i=0;i<byteChars.length;i++) byteNumbers[i] = byteChars.charCodeAt(i);
            const blob = new Blob([new Uint8Array(byteNumbers)], { type:'application/pdf' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank','noopener');
            setTimeout(()=> URL.revokeObjectURL(url), 60_000);
        } catch(err){
            console.error('Dokument Abruf fehlgeschlagen', err);
            // TODO: notificationStore.addNotification(...) wenn gewünscht
        }
    }

    // handling the contact status
    function handleChangeStatus(statusSection, selectedOption) {
        // console.log('@handleChangeStatus', statusSection, selectedOption);
        userStore.currentSelection.businessPartner.hasValid.salesPitch = false;
        Object.values(status['Entscheider Kontakt hergestellt'].options).forEach((option) => option.isParent= false )

        if (!['Auswahl Datum', 'Auswahl Zeit'].includes(statusSection)) {
            currentStatusSection.value = statusSection; 
            currentSelectedOption.value = selectedOption;
            // reset all checked values
            Object.values(status[statusSection].options).forEach((o) => o.checked= false );
            status[statusSection].options[selectedOption].checked = true;
        }
        
        if (statusSection == 'Beendet') {
            status['Beendet'].options[selectedOption].checked = true;

            status['Verbindlicher Termin mit Entscheider'].options['Rückruf'].checked = false
            status['Verbindlicher Termin mit Entscheider'].options['Termin vor Ort'].checked = false
        }

        if (statusSection == 'Entscheider Kontakt hergestellt') {
            status['Beendet'].display = true
            Object.values(status['Beendet'].options).forEach((option) => option.display= false );
            Object.values(status['Beendet'].options).forEach((option) => option.checked= false );
            status['Entscheider Kontakt hergestellt'].options[selectedOption].isParent = true;

            if (selectedOption === 'Nein') {
                status.allowNext = true;
                status['Verbindlicher Termin mit Entscheider'].display = false
                status['Unverbindliche Wiedervorlage'].display = true
                // Alle Optionen sichtbar machen (es gibt aktuell nur eine)
                Object.values(status['Unverbindliche Wiedervorlage'].options).forEach(o => o.display = true);
                for (const key of ['Geschäftsaufgabe', 'Entscheider nie erreicht']) {
                    status['Beendet'].options[key].display = true;
                }
            }
            if (selectedOption === 'Ja, telefonisch' || selectedOption === 'Ja, vor Ort') {
                status.allowNext = true;
                for (const key of ['Geschäftsaufgabe', 'Entscheider desinteressiert']) {
                    status['Beendet'].options[key].display = true;
                }
                if (!Object.values(status['Beendet'].options).find((option) => option.checked)) status['Verbindlicher Termin mit Entscheider'].display = true
                if (!Object.values(status['Beendet'].options).find((option) => option.checked)) status['Unverbindliche Wiedervorlage'].display = true
                if (status['Verbindlicher Termin mit Entscheider'].display) Object.values(status['Verbindlicher Termin mit Entscheider'].options).forEach(o => o.display = true);
                if (status['Unverbindliche Wiedervorlage'].display) Object.values(status['Unverbindliche Wiedervorlage'].options).forEach(o => o.display = true);
            }
            if (selectedOption === 'Ungültig: Dublette' || selectedOption === 'Ungültig: kein Anschluss') {
                status.allowNext = false;
                status['Verbindlicher Termin mit Entscheider'].display = false
                status['Unverbindliche Wiedervorlage'].display = false
            }
        }

        if (statusSection == 'Verbindlicher Termin mit Entscheider') {
            if (selectedOption === 'Rückruf') {
                status['Verbindlicher Termin mit Entscheider'].display = true
                status['Unverbindliche Wiedervorlage'].display = true
                Object.assign(status.datePicker['verbindlich'], {  title: 'Rückruf', inputTime: 'required', addendum: '=> Google-Kalender (bitte prüfen)' })
                Object.assign(status.datePicker['unverbindlich'], {  selected: {date: null, time: null} })
                Object.values(status['Verbindlicher Termin mit Entscheider'].options).forEach(o => o.display = true);
                Object.values(status['Unverbindliche Wiedervorlage'].options).forEach(o => o.display = true);
            }
            if (selectedOption === 'Termin vor Ort') {
                status['Verbindlicher Termin mit Entscheider'].display = true
                status['Unverbindliche Wiedervorlage'].display = true
                {
                    let chosen = null;
                    const bpContacts = businessPartnersStore.businessPartner?.contacts;
                    if (isUnknownDecisionMakerSelected()) {
                        chosen = null;
                    } else if (Array.isArray(bpContacts)) {
                        chosen = bpContacts.find(c => String(c.id) === String(selectedContactId.value)) || bpContacts[0];
                    } else {
                        chosen = bpContacts;
                    }
                    const candidateEmail = isUnknownDecisionMakerSelected()
                        ? 'nicht bekannt'
                        : (chosen?.email || (Array.isArray(chosen?.emailAddresses) ? chosen.emailAddresses[0] : undefined) || (Array.isArray(chosen?.emailList) ? chosen.emailList[0]?.name : undefined) || '').trim();
                    Object.assign(status.datePicker['verbindlich'], {  title: 'Termin vor Ort', inputTime: 'required', addendum: `=> Google-Kalender (bitte prüfen), Terminbestätigung an ${candidateEmail || '—'}` })
                }
                Object.assign(status.datePicker['unverbindlich'], {  selected: {date: null, time: null} })
                Object.values(status['Verbindlicher Termin mit Entscheider'].options).forEach(o => o.display = true);
                Object.values(status['Unverbindliche Wiedervorlage'].options).forEach(o => o.display = true);
            }
        }

        if (statusSection == 'Unverbindliche Wiedervorlage') {
            if (selectedOption === 'Datum eintragen') {
                status['Unverbindliche Wiedervorlage'].display = true

                status['Verbindlicher Termin mit Entscheider'].options['Rückruf'].checked = false
                status['Verbindlicher Termin mit Entscheider'].options['Termin vor Ort'].checked = false

                Object.assign(status.datePicker['unverbindlich'], {  title: 'Wiedervorlage für', inputTime: 'optional', addendum: '(Optional)' })
                Object.assign(status.datePicker['verbindlich'], {  selected: {date: null, time: null} })
                // Optionen sichtbar machen
                Object.values(status['Unverbindliche Wiedervorlage'].options).forEach(o => o.display = true);
            }
        }
        checkSalesPitchIsValid();
    }

    function resetSalesPitchFormState() {
        status.currentStep = 1;
        status.allowNext = false;
        status.allowPrevious = false;
        status.history = [];

        currentStatusSection.value = '';
        currentSelectedOption.value = '';
        currentComment.value = '';

        status['Entscheider Kontakt hergestellt'].display = true;
        status['Beendet'].display = false;
        status['Verbindlicher Termin mit Entscheider'].display = false;
        status['Unverbindliche Wiedervorlage'].display = false;

        ['Entscheider Kontakt hergestellt', 'Beendet', 'Verbindlicher Termin mit Entscheider', 'Unverbindliche Wiedervorlage'].forEach((sectionName) => {
            Object.values(status[sectionName].options).forEach((option) => {
                option.checked = false;
                option.display = true;
                if (Object.prototype.hasOwnProperty.call(option, 'isParent')) {
                    option.isParent = false;
                }
            });
        });

        status.datePicker.unverbindlich.selected = { date: null, time: null };
        status.datePicker.verbindlich.selected = { date: null, time: null };
        userStore.currentSelection.businessPartner.hasValid.salesPitch = false;
        salesPitchStatusRenderKey.value += 1;
    }


    // Funktionen zum Navigieren zwischen den Schritten
    function handleGoToNextStep() {
        status.currentStep++
        status.allowNext = false;
        status.allowPrevious = (status.currentStep!==1); 
        const selectedOption = status[currentStatusSection.value]?.options?.[currentSelectedOption.value];
        // Merke neben dem Objekt auch den Key (Label) für spätere ID-Mapping (Parent-ID Ermittlung)
        status.history.push({ key: currentSelectedOption.value, option: selectedOption });
        // console.log('moving to next step',status.currentStep, 'history is', status.history)

    }

    function handleGoToPreviousStep() {
        status.currentStep--
        status.allowNext = false;
        status.allowPrevious = (status.currentStep!==1); 
        // remove the last entry from the status history
        status.history.pop();
        status.datePicker['unverbindlich'].selected= { date: null, time: null }
        status.datePicker['verbindlich'].selected= { date: null, time: null }      
        currentStatusSection.value = '';
        currentSelectedOption.value = '';
        // console.log('moving to previous step',status.currentStep, 'history is', status.history)
        checkSalesPitchIsValid();     
    }

    function checkSalesPitchIsValid() {
        // Reset requirement flags
        decisionMakerNeeded.value = false;
        potentialNeeded.value = false;
        dateNeeded.value = false;
        timeNeeded.value = false;
        let valid = false;

        // Sofort Abschluss: sichtbar bei Ja-Optionen auf Schritt 1, aber nur enabled wenn Entscheider gültig gewählt
        const isImmediateCloseContext = (status.currentStep === 1 && currentStatusSection.value === 'Entscheider Kontakt hergestellt' && ['Ja, telefonisch','Ja, vor Ort'].includes(currentSelectedOption.value));
        // enabled Zustand erst später nach Entscheider-Validierung setzen

        // Entscheider Prüfung nur wenn benötigt.
        // Regel: Eine Entscheider-E-Mail ist nur Pflicht, wenn tatsächlich ein verbindlicher Termin
        // (Rückruf/Termin vor Ort) vereinbart wird bzw. beim Sofort-Abschluss. Für Wiedervorlage/Beendet
        // wird keine E-Mail benötigt.
        decisionMakerInvalid.value = false;
        const optionNeedsDecisionMaker = (currentStatusSection.value === 'Entscheider Kontakt hergestellt' && ['Ja, telefonisch','Ja, vor Ort'].includes(currentSelectedOption.value))
            || (currentStatusSection.value === 'Verbindlicher Termin mit Entscheider' && ['Rückruf','Termin vor Ort'].includes(currentSelectedOption.value));
        decisionMakerNeeded.value = optionNeedsDecisionMaker && !currentSelectedOption.value?.startsWith('Ungültig:');
        if (decisionMakerNeeded.value) {
            if (isUnknownDecisionMakerSelected()) {
                decisionMakerInvalid.value = false;
            } else if (contacts && contacts.value && contacts.value.length > 0) {
                if (!selectedContactId.value && contacts.value.length === 1) selectedContactId.value = contacts.value[0].id;
                const chosen = contacts.value.find(c => String(c.id) === String(selectedContactId.value));
                const candidateEmail = (chosen?.email || (Array.isArray(chosen?.emailAddresses) ? chosen.emailAddresses[0] : undefined) || (Array.isArray(chosen?.emailList) ? chosen.emailList[0]?.name : undefined) || '').trim();
                if (!chosen || !isValidEmail(candidateEmail)) {
                    decisionMakerInvalid.value = true;
                }
            } else {
                decisionMakerInvalid.value = true; // keine Kontakte vorhanden
            }
        }
        // Jetzt enable Zustand setzen
        immediateCloseEnabled.value = isImmediateCloseContext && (!decisionMakerNeeded.value || (decisionMakerNeeded.value && !decisionMakerInvalid.value));

        // STEP 1 Rules (only Ungültig can save; Ja/Nein just proceed)
        if (status.currentStep === 1) {
            if (currentStatusSection.value === 'Entscheider Kontakt hergestellt' && currentSelectedOption.value?.startsWith?.('Ungültig:')) {
                valid = true; // Ungültig sofort speicherbar
            } else {
                valid = false; // Andere gehen über Weiter
            }
        }

        // STEP 2 Rules
        if (status.currentStep === 2) {
            if (currentStatusSection.value === 'Beendet') {
                valid = !(decisionMakerNeeded.value && decisionMakerInvalid.value);
            } else if (currentStatusSection.value === 'Unverbindliche Wiedervorlage' && currentSelectedOption.value==='Datum eintragen') {
                dateNeeded.value = true;
                potentialNeeded.value = false; // Potentiale sind hier NICHT erforderlich
                if (!status.datePicker['unverbindlich'].selected.date) valid = false; else valid = true;
                // Keine Potential-Prüfung bei unverbindlicher Wiedervorlage
                if (decisionMakerNeeded.value && decisionMakerInvalid.value) valid = false;
            } else if (currentStatusSection.value === 'Verbindlicher Termin mit Entscheider' && ['Rückruf','Termin vor Ort'].includes(currentSelectedOption.value)) {
                dateNeeded.value = true;
                timeNeeded.value = true; // Zeit immer required für Rückruf & Termin vor Ort
                potentialNeeded.value = true;
                if (!status.datePicker['verbindlich'].selected.date) valid = false; else valid = true;
                if (!status.datePicker['verbindlich'].selected.time) valid = false;
                if (offersForSalesPitch.value.length === 0) valid = false;
                if (decisionMakerNeeded.value && decisionMakerInvalid.value) valid = false;
            }
        }

        userStore.currentSelection.businessPartner.hasValid.salesPitch = valid;
    }

    function getBusinessPartnerWithSalesPitch() {
        const bp = businessPartnersStore.businessPartner;
        if (!bp) return null;
        if (!bp.salesPitch) bp.salesPitch = {};
        if (!Array.isArray(bp.salesPitch.offerList)) bp.salesPitch.offerList = [];
        return bp;
    }

    async function handleSaveSalesPitch(commentFromChild) {
        // console.log(status.datePicker)
        saveSalesPitchIsLoading.value = true;
        if (decisionMakerNeeded.value && decisionMakerInvalid.value) {
            saveSalesPitchIsLoading.value = false;
            return;
        }
        // Parent (Step 1) sicher bestimmen per Mapping Tabelle statt Suche in Filter-Optionen (die enthalten nur aggregierte Kontaktstatus)
        const step1Key = (Array.isArray(status.history) && status.history.length > 0) ? status.history[0]?.key : null;
        const parentId = step1Key ? SALES_PITCH_STATUS_IDS.step1[step1Key] || null : null;
        
        const bp = getBusinessPartnerWithSalesPitch();
        if (!bp) {
            saveSalesPitchIsLoading.value = false;
            return;
        }
        const additionalSalesPitch = {
            id: null,
            comment: commentFromChild ?? currentComment.value,
            offerList: (bp.salesPitch.offerList || []).map(o => ({
                id: o.id ?? null,
                idPublication: o.idPublication ?? -1,
                publication: o.publication ?? null,
                product: {
                    id: normalizeProductId(o.product?.id),
                    name: o.product?.name,
                    shortcut: o.product?.shortcut
                },
                status: { id: o.status?.id },
                selfCreated: !!o.selfCreated,
                refusalReason: o.refusalReason ?? undefined
            })),
        };
        // set contact information as they may have changed
        let contact = bp.contacts;
        if (isUnknownDecisionMakerSelected()) {
            contact = null;
        } else if (Array.isArray(bp.contacts)) {
            contact = bp.contacts.find(c => c.id === selectedContactId.value) || bp.contacts[0];
        }
        additionalSalesPitch.contact = contact;
        // Mapping der aktuellen Auswahl auf Backend Status IDs
        if (currentStatusSection.value === 'Beendet') {
            const id = SALES_PITCH_STATUS_IDS.beendet[currentSelectedOption.value];
            Object.assign(additionalSalesPitch, {
                status: { id, idParent: parentId },
                dateFollowUp: null,
            });
            if (currentSelectedOption.value === 'Entscheider desinteressiert') {
                // alle Angebote werden abgelehnt
                additionalSalesPitch.offerList.forEach((offer) => {
                    offer.status.id = 21; // Abgelehnt
                    offer.refusalReason = 'Entscheider desinteressiert';
                });
            }
        } else if (currentStatusSection.value === 'Entscheider Kontakt hergestellt') {
            // Invalid Fälle speichern ohne Parent
            if (SALES_PITCH_STATUS_IDS.invalid[currentSelectedOption.value]) {
                const id = SALES_PITCH_STATUS_IDS.invalid[currentSelectedOption.value];
                Object.assign(additionalSalesPitch, {
                    status: { id, idParent: null },
                    dateFollowUp: null,
                });
            } else {
                // Eigentlich kein Speichervorgang hier (valid = false außer Ungültig) – defensiver Fallback
                if (parentId) {
                    Object.assign(additionalSalesPitch, {
                        status: { id: parentId, idParent: null },
                        dateFollowUp: null,
                    });
                }
            }
        } else if (currentStatusSection.value === 'Unverbindliche Wiedervorlage') {
            // Align with new flow: Wiedervorlage uses the base (step1) as status.id and no parent
            Object.assign(additionalSalesPitch, {
                status: { id: parentId, idParent: null },
                dateFollowUp: `${status.datePicker['unverbindlich'].selected.date}T${(status.datePicker['unverbindlich'].selected.time) ? status.datePicker['unverbindlich'].selected.time : '00:00'}:00.000Z`,
            });
        } else if (currentStatusSection.value === 'Verbindlicher Termin mit Entscheider') {
            const id = SALES_PITCH_STATUS_IDS.termin[currentSelectedOption.value];
            Object.assign(additionalSalesPitch, {
                status: { id, idParent: parentId },
                dateFollowUp: `${status.datePicker['verbindlich'].selected.date}T${(status.datePicker['verbindlich'].selected.time) ? status.datePicker['verbindlich'].selected.time : '00:00'}:00.000Z`,
            });
            additionalSalesPitch.offerList.forEach((offer) => { offer.status.id = 23; }); // Terminiert
        }
        // console.log('saving salesPitch', additionalSalesPitch)
        userStore.currentSelection.businessPartner.additionalSalesPitches.push(additionalSalesPitch);

        userStore.currentSelection.businessPartner.isLoading = true;
        try {
            const saveSalesPitchResult = await businessPartnersStore.saveSalesPitch(additionalSalesPitch);

            // console.log('salesPitch to save', additionalSalesPitch);
            await userStore.refreshAppointments()
            if (saveSalesPitchResult) {
                userStore.currentSelection.businessPartner.isLoading = false;
                resetSalesPitchFormState();
                const currentGpNoAfterSave =
                    businessPartnersStore.businessPartner?.gpNo
                    || businessPartnersStore.businessPartner?.gp?.no
                    || (String(route.query.searchMode) === 'GPNo' ? route.query.search : null);

                if (canNavigateWithinList.value && hasNextBusinessPartner.value) {
                    // Spinner anzeigen und weiternavigieren
                    isOpeningNextCustomer.value = true;
                    const beforePath = route.fullPath;
                    try {
                        await navigateRelativeInList(1);
                    } finally {
                        isOpeningNextCustomer.value = false;
                    }
                    if (route.fullPath !== beforePath) {
                        return;
                    }
                }
                // Liste nach dem Speichern neu laden
                await businessPartnersStore.getByFilter(
                    userStore.currentFilter.filter,
                    userStore.currentFilter.searchCriteria
                );

                // Wenn kein nächster Kunde existiert (oder Navigation nicht stattfindet),
                // den aktuellen Detaildatensatz explizit neu laden, damit Statusfelder aktualisiert sind.
                if (currentGpNoAfterSave) {
                    const refreshedDetail = await businessPartnersStore.loadDetailByGpNo(
                        currentGpNoAfterSave,
                        businessPartnersStore.businessPartner?.id || null
                    );
                    if (refreshedDetail) {
                        businessPartnersStore.businessPartner = refreshedDetail;
                    }
                }

                await resetBusinessPartnerScrollToTop();
            }
        } finally {
            saveSalesPitchIsLoading.value = false;
            userStore.currentSelection.businessPartner.isLoading = false;
        }
    }

    async function handleImmediateClose() {
        if (!immediateCloseEnabled.value || immediateCloseLoading.value) return;
        if (decisionMakerNeeded.value && decisionMakerInvalid.value) return;
        immediateCloseLoading.value = true;
        // Determine status id: 29 = persönlich (vor Ort), 30 = telefonisch
    const isVorOrt = currentSelectedOption.value === 'Ja, vor Ort';
        const statusId = isVorOrt ? 29 : 30;
        // aktuelles Datum + Uhrzeit (UTC) für dateFollowUp setzen
        const now = new Date();
        const isoNow = now.toISOString(); // z.B. 2025-09-25T10:15:30.123Z
                const bp = getBusinessPartnerWithSalesPitch();
                if (!bp) {
                    immediateCloseLoading.value = false;
                    return;
                }
                const additionalSalesPitch = {
                        id: null,
                        comment: currentComment.value,
                        offerList: bp.salesPitch.offerList.map(o => ({
                            ...o,
                            product: {
                                ...o.product,
                                id: normalizeProductId(o.product?.id)
                            }
                        })),
                        contact: isUnknownDecisionMakerSelected()
                            ? null
                            : (Array.isArray(bp.contacts)
                                ? bp.contacts.find(c => c.id === selectedContactId.value) || bp.contacts[0]
                                : bp.contacts),
                        status: { id: statusId, idParent: null },
                        dateFollowUp: isoNow
                };
                // Produkte direkt als abgeschlossen markieren (persönlich = 19, telefonisch = 20)
                additionalSalesPitch.offerList.forEach(o => { o.status.id = isVorOrt ? 19 : 20; });
        // In Array pushen wie beim normalen Speichern
        userStore.currentSelection.businessPartner.additionalSalesPitches.push(additionalSalesPitch);
        userStore.currentSelection.businessPartner.isLoading = true;
        // Speichern mit Payload (vorher wurde ohne Parameter aufgerufen)
        const saveResult = await businessPartnersStore.saveSalesPitch(additionalSalesPitch);
        userStore.currentSelection.businessPartner.isLoading = false;
        immediateCloseLoading.value = false;
        if (saveResult) {
            await userStore.refreshAppointments();
            try {
                router.push({ name: 'SalesPitchFeedback' });
            } catch(e){
                navigateBack();
            }
        }
    }

    function handleAddProduct() {
        const bp = getBusinessPartnerWithSalesPitch();
        if (!bp) return false;
        const normId = normalizeProductId(newProductForAppointment.value);
        if (!Number.isFinite(normId) || normId === -1) return false;
        const pool = availablePotentialProducts.value || [];
        const selectedProduct = pool.find((product) => normalizeProductId(product?.id) === normId)
            || (Array.isArray(userStore.filterOption?.businessPartnerPotential) 
                ? userStore.filterOption.businessPartnerPotential.find((product) => normalizeProductId(product?.id) === normId) 
                : null);
        if (!selectedProduct) return false;
        const offerList = bp.salesPitch.offerList;
        const productExists = offerList.find((offer) => normalizeProductId(offer?.product?.id) === normalizeProductId(selectedProduct?.id));
        if (productExists) return false;
        const newOffer = {
            id: null,
            idPublication: -1,
            publication: null,
            product: {
                ...selectedProduct,
                id: normalizeProductId(selectedProduct.id)
            },
            status: { id: 16 },
            selfCreated: true
        };
        offerList.push(newOffer);
        offerListCopy.value.push(JSON.parse(JSON.stringify(newOffer)));
        newProductForAppointment.value = -1;
        checkSalesPitchIsValid();
    }

    function handleRemoveProduct(id) {
        const bp = getBusinessPartnerWithSalesPitch();
        if (!bp) return false;
        const productExists = bp.salesPitch.offerList.find((offer) => offer.product.id === id);
        if (!productExists) return false;
        bp.salesPitch.offerList = bp.salesPitch.offerList.filter((offer) => offer.product.id !== id);
        offerListCopy.value = offerListCopy.value.filter((offer) => offer.product.id !== id);
        checkSalesPitchIsValid();
    }

    // Explicit handler to avoid template ref auto-unwrapping issues
    function handleUpdateNewProductForAppointment(v) {
        // Accept numeric id or strings like "product:123"
        const id = normalizeProductId(v);
        newProductForAppointment.value = Number.isFinite(id) ? id : -1;
    }

    function handleAbortSalesPitch() {
        // console.log('handleAbortSalesPitch');
        navigateBack();
    }

    function isQueryTrue(value) {
        return ['1', 'true', 'True', true].includes(value);
    }

    const listNavigationEntries = computed(() => {
        const list = businessPartnersStore.businessPartnerList?.data;
        return Array.isArray(list) ? list : [];
    });

    const currentListIndex = computed(() => {
        const currentId = userStore.currentSelection.businessPartner?.id;
        const currentGpNo = businessPartnersStore.businessPartner?.gpNo
            || (String(route.query.searchMode) === 'GPNo' ? route.query.search : null);
        const list = listNavigationEntries.value;
        if (!list.length) return -1;

        let index = -1;
        if (currentId !== undefined && currentId !== null) {
            index = list.findIndex((bp) => String(bp?.id) === String(currentId));
        }
        if (index === -1 && currentGpNo) {
            index = list.findIndex((bp) => String(bp?.gpNo) === String(currentGpNo));
        }
        if (index === -1 && route.query.position !== undefined && route.query.position !== null && route.query.position !== '') {
            const fromPosition = Number(route.query.position);
            if (Number.isInteger(fromPosition) && fromPosition >= 0) {
                index = fromPosition;
            }
        }
        return index;
    });

    const canNavigateWithinList = computed(() => {
        if (!listNavigationEntries.value.length) return false;
        return isQueryTrue(route.query.fromList)
            || isQueryTrue(route.query.fromTodayVorOrtTermineList)
            || isQueryTrue(route.query.fromTodayWiedervorlagenList);
    });

    const listNavigationMaxRows = computed(() => Number(userStore.currentSelection.businessPartnerList?.maxRows || 0));
    const navigationCursorIndex = computed(() => {
        const queryPosition = Number(route.query.position);
        if (Number.isInteger(queryPosition) && queryPosition >= 0) return queryPosition;
        return currentListIndex.value;
    });
    const hasPreviousBusinessPartner = computed(() => canNavigateWithinList.value && navigationCursorIndex.value > 0);
    const hasNextBusinessPartner = computed(() => {
        if (!canNavigateWithinList.value || navigationCursorIndex.value < 0) return false;
        const loadedTotal = listNavigationEntries.value.length;
        const availableTotal = listNavigationMaxRows.value > 0 ? listNavigationMaxRows.value : loadedTotal;
        return navigationCursorIndex.value < (availableTotal - 1);
    });
    const listNavigationTotal = computed(() => listNavigationEntries.value.length);

    const previousBusinessPartner = computed(() => {
        if (!hasPreviousBusinessPartner.value) return null;
        return listNavigationEntries.value[navigationCursorIndex.value - 1] || null;
    });

    const nextBusinessPartner = computed(() => {
        if (!hasNextBusinessPartner.value) return null;
        return listNavigationEntries.value[navigationCursorIndex.value + 1] || null;
    });

    function navigationDisplayName(bp) {
        return bp?.name || bp?.companyName || bp?.gp?.name || `GP ${bp?.gpNo || bp?.gp?.no || '—'}`;
    }

    function formatNavigationCount(value) {
        const number = Number(value || 0);
        if (!Number.isFinite(number) || number <= 0) return '—';
        try {
            return number.toLocaleString('de-DE');
        } catch (error) {
            return String(number);
        }
    }

    // Declared here (ahead of its "Sales pitch flow state" siblings further down)
    // because the immediate watchers below call prefetchNextBusinessPartnerIfNeeded()
    // synchronously during setup, before that later block would have run.
    const saveSalesPitchIsLoading = ref(false);

    async function prefetchNextBusinessPartnerIfNeeded() {
        if (!canNavigateWithinList.value) return;
        if (isSwitchingBusinessPartner.value || isNavigatingBack.value || saveSalesPitchIsLoading.value) return;
        if (userStore.currentSelection.businessPartnerList?.isLoading) return;

        const qp = Number(route.query.position);
        const resolvedIndex = navigationCursorIndex.value >= 0
            ? navigationCursorIndex.value
            : (Number.isInteger(qp) && qp >= 0 ? qp : -1);
        if (resolvedIndex < 0) return;

        const loadedTotal = listNavigationEntries.value.length;
        const availableTotal = listNavigationMaxRows.value > 0 ? listNavigationMaxRows.value : loadedTotal;
        if (loadedTotal === 0) return;
        if (loadedTotal >= availableTotal) return;
        if (resolvedIndex < loadedTotal - 2) return;

        const expanded = await businessPartnersStore.getByFilterExpand();
        return expanded !== false;
    }

    const isBusinessPartnerListLoading = computed(() => userStore.currentSelection.businessPartnerList?.isLoading === true);

    const previousPositionLabel = computed(() => {
        if (!hasPreviousBusinessPartner.value) return '—';
        const availableTotal = listNavigationMaxRows.value > 0 ? listNavigationMaxRows.value : listNavigationTotal.value;
        return `${navigationCursorIndex.value}/${formatNavigationCount(availableTotal)}`;
    });

    const nextPositionLabel = computed(() => {
        if (!hasNextBusinessPartner.value) return '—';
        const availableTotal = listNavigationMaxRows.value > 0 ? listNavigationMaxRows.value : listNavigationTotal.value;
        return `${navigationCursorIndex.value + 2}/${formatNavigationCount(availableTotal)}`;
    });

    function truncateNavLabel(value, maxLength = 28) {
        const text = String(value || '').trim();
        if (!text) return '—';
        if (text.length <= maxLength) return text;
        const sliced = text.slice(0, maxLength - 1).trimEnd();
        const lastSpace = sliced.lastIndexOf(' ');
        const bestCut = lastSpace > Math.floor(maxLength * 0.6)
            ? sliced.slice(0, lastSpace)
            : sliced;
        return `${bestCut}…`;
    }

    function navigationMetaLabel(bp) {
        return `GP: ${bp?.gpNo || '—'}`;
    }

    function isTextInputTarget(target) {
        if (!target) return false;
        const tag = String(target.tagName || '').toLowerCase();
        return target.isContentEditable || ['input', 'textarea', 'select'].includes(tag);
    }

    function handleBusinessPartnerNavigationHotkey(event) {
        if (!canNavigateWithinList.value || isSwitchingBusinessPartner.value || isNavigatingBack.value || saveSalesPitchIsLoading.value) return;
        if (isTextInputTarget(event.target)) return;

        if (event.key === 'ArrowLeft' && hasPreviousBusinessPartner.value) {
            event.preventDefault();
            navigateRelativeInList(-1);
        }
        if (event.key === 'ArrowRight' && hasNextBusinessPartner.value) {
            event.preventDefault();
            navigateRelativeInList(1);
        }
    }

    function handlePreviewHeaderBackClick(event) {
        const trigger = event?.target?.closest?.('.ph-btn');
        if (!trigger) return;

        isNavigatingBack.value = true;
        const originRoute = route.fullPath;

        if (previewHeaderBackResetTimer) {
            clearTimeout(previewHeaderBackResetTimer);
            previewHeaderBackResetTimer = null;
        }

        // Fallback: if navigation is canceled and we are still on the same route, hide overlay again.
        previewHeaderBackResetTimer = setTimeout(() => {
            if (route.fullPath === originRoute) {
                isNavigatingBack.value = false;
            }
            previewHeaderBackResetTimer = null;
        }, 10000);
    }

    onMounted(() => {
        window.addEventListener('keydown', handleBusinessPartnerNavigationHotkey);
    });

    watch(
        [currentListIndex, listNavigationEntries, listNavigationMaxRows, canNavigateWithinList],
        () => {
            void prefetchNextBusinessPartnerIfNeeded();
        },
        { deep: true, immediate: true, flush: 'post' }
    );

    watch(
        isBusinessPartnerListLoading,
        (isLoading, wasLoading) => {
            if (wasLoading && !isLoading) {
                void prefetchNextBusinessPartnerIfNeeded();
            }
        },
        { immediate: true }
    );

    onUnmounted(() => {
        window.removeEventListener('keydown', handleBusinessPartnerNavigationHotkey);
        if (previewHeaderBackResetTimer) {
            clearTimeout(previewHeaderBackResetTimer);
            previewHeaderBackResetTimer = null;
        }
    });

    async function resetBusinessPartnerScrollToTop() {
        await nextTick();

        if (document.activeElement && typeof document.activeElement.blur === 'function') {
            document.activeElement.blur();
        }

        const resetTargets = () => {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

            const dynamicTargets = Array.from(document.querySelectorAll(
                '.singlepage-wrapper, #container-main, .widget-wrapper, .container-wrapper, .scroll-y, .accordion-content-wrapper, .customer-details-content'
            ));

            const scrollContainers = [
                document.scrollingElement,
                document.documentElement,
                document.body,
                document.querySelector('.widget-wrapper.potential-bk.customer-details'),
                document.querySelector('.widget-wrapper.potential-bk.customer-details .container-wrapper'),
                ...dynamicTargets,
            ];

            scrollContainers.forEach((el) => {
                if (!el) return;
                if (typeof el.scrollTo === 'function') {
                    el.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                }
                if (typeof el.scrollTop === 'number') {
                    el.scrollTop = 0;
                }
            });
        };

        resetTargets();
        requestAnimationFrame(() => {
            resetTargets();
            requestAnimationFrame(() => {
                resetTargets();
            });
        });
    }

    async function navigateRelativeInList(step) {
        const currentIndex = navigationCursorIndex.value;
        const targetIndex = currentIndex + step;
        if (currentIndex < 0 || targetIndex < 0) return;

        if (step > 0) {
            await prefetchNextBusinessPartnerIfNeeded();
            const maxRows = Number(userStore.currentSelection.businessPartnerList?.maxRows || 0);
            while (targetIndex >= listNavigationEntries.value.length && listNavigationEntries.value.length < maxRows) {
                const expanded = await businessPartnersStore.getByFilterExpand();
                if (expanded === false) return;
            }
        }

        if (targetIndex >= listNavigationEntries.value.length) return;

        const target = listNavigationEntries.value[targetIndex];
        if (!target?.gpNo) return;

        const nextQuery = {
            ...route.query,
            searchMode: 'GPNo',
            search: String(target.gpNo),
            fromList: 'true',
            position: String(targetIndex),
        };

        isSwitchingBusinessPartner.value = true;
        try {
            await router.push({ name: 'BusinessPartner', query: nextQuery });
            // Beim Kundenwechsel den Kontaktstatus-Flow sauber neu starten,
            // damit Seite 2/gewählte Optionen nicht auf den nächsten Kunden durchschlagen.
            resetSalesPitchFormState();
            await resetBusinessPartnerScrollToTop();
            // Nach Navigation Detail des neuen Kunden explizit frisch laden,
            // damit gecachte Kontakthistorie überschrieben wird.
            if (target.gpNo) {
                try {
                    const freshDetail = await businessPartnersStore.loadDetailByGpNo(String(target.gpNo), target.id ?? null);
                    if (freshDetail) {
                        businessPartnersStore.businessPartner = freshDetail;
                    }
                } catch (e) {
                    // Fehler hier nicht blockend – Navigation ist bereits erfolgt
                }
            }
        } finally {
            isSwitchingBusinessPartner.value = false;
        }
    }

    function getBackNavigationTarget() {
        const businessPartnerGpNo = businessPartnersStore.businessPartner?.gpNo;
        const position = route.query.position;
        const fromList = route.query.fromList === 'true';
        const fromTodayVorOrtTermineList = route.query.fromTodayVorOrtTermineList === 'true';
        const fromTodayWiedervorlagenList = route.query.fromTodayWiedervorlagenList === 'true';
        const fromResearchList = route.query.fromResearchList === 'true';

        // Suchkontext aus der aktuellen Route (z.B. direkte Suche aus Dashboard/Preview)
        const routeSearchMode = route.query.searchMode;
        const routeSearchQuery = route.query.search;
        const cameFromSearchRoute = !!routeSearchMode && !!routeSearchQuery;

        // Such-/Quellkontext der BusinessPartner-Liste aus dem Store
        const listSource = userStore.currentSelection.businessPartnerList.source;
        const cameFromSearchList = listSource === 'search';
        const listSearch = userStore.currentSelection.businessPartnerList.search || {};
        const listSearchMode = listSearch.mode;
        const listSearchQuery = listSearch.query;

        // Debug: vollständigen Kontext und aktuellen Filterzustand beim Zurücknavigieren loggen
        try {
            const cfSnapshot = (() => {
                try {
                    const cf = userStore.currentFilter;
                    return cf ? {
                        signature: cf.filter?.signature,
                        salesValues: cf.filter?.salesValues,
                        businessPartnerSalesPitchStatus: cf.filter?.businessPartnerSalesPitchStatus,
                        invoice: cf.filter?.invoice,
                        page: cf.filter?.page,
                        pages: cf.filter?.pages,
                        pageSize: cf.filter?.pageSize,
                    } : null;
                } catch (e) { return null; }
            })();
        } catch(e) { /* debug logging only */ }

        const directSingleFromSearch = cameFromSearchRoute && !fromList && !fromTodayVorOrtTermineList && !fromTodayWiedervorlagenList && !fromResearchList;
        // Nur direkte Einzel-GPNo-Suche (nicht über Liste / Today / Research) führt direkt zum Dashboard.
        const isSingleGpNoSearchDirect = (
            routeSearchMode === 'GPNo' && cameFromSearchRoute && businessPartnersStore.businessPartnerList?.size === 1 &&
            !fromList && !fromTodayVorOrtTermineList && !fromTodayWiedervorlagenList && !fromResearchList
        );
        if (isSingleGpNoSearchDirect) {
            return { name: 'Dashboard', hash: '#potential-bk' };
        }

        // Bestehenden Listen-Filter (mit Signatur) wiederverwenden, damit der Filterzustand beim Zurückkehren erhalten bleibt
        const cf = userStore.currentFilter;
        const filterSignature = cf && cf.filter ? cf.filter.signature : undefined;

        let query = {
            source: 'filter',
            // explizit die Filter-Signatur übergeben, damit der Router-Guard den Filter nicht als "generisch" behandelt
            ...(filterSignature ? { filter: filterSignature } : {}),
            businessPartnerGpNo: businessPartnerGpNo,
            ...(position ? { position } : {})
        };

        if (fromTodayVorOrtTermineList) {
            query.namedFilter = 'Today:VorOrtTermine';
            query.hideFilter = 'true';
        }
        if (fromTodayWiedervorlagenList) {
            query.namedFilter = 'Today:Wiedervorlagen';
            query.hideFilter = 'true';
        }

        if (fromList || fromTodayVorOrtTermineList || fromTodayWiedervorlagenList) {
            // Wenn wir aus einer Such-Liste (source === 'search') kommen, denselben Suchkontext wiederherstellen
            if (cameFromSearchList) {
                return {
                    name: 'BusinessPartnerList',
                    hash: businessPartnerGpNo ? ('#' + businessPartnerGpNo) : undefined,
                    query: {
                        source: 'search',
                        searchMode: listSearchMode,
                        search: listSearchQuery,
                        wholeWord: listSearch.wholeWord ? 1 : 0,
                        ...(position ? { position } : {})
                    }
                };
            } else {
                let listFilterSnapshot = null;
                if (typeof route.query.listFilter === 'string' && route.query.listFilter.trim()) {
                    try {
                        listFilterSnapshot = JSON.parse(route.query.listFilter);
                    } catch (e) {
                        listFilterSnapshot = null;
                    }
                }
                // Andernfalls kam die Liste aus einem Filter – Filter-Signatur wiederverwenden
                return {
                    path: '/businessPartnerList',
                    hash: '#' + businessPartnerGpNo,
                    query: {
                        ...query,
                        ...(listFilterSnapshot ? { filter: JSON.stringify(listFilterSnapshot) } : {})
                    }
                };
            }
        } else {
            if(fromResearchList) {
                const requestedSignature = typeof route.query.signature === 'string' ? route.query.signature : '';
                const hasRequestedResearchList = requestedSignature
                    ? leadGenerationStore.leadLists.some((list) => list.signature === requestedSignature)
                    : false;
                const fallbackResearchSignature = leadGenerationStore.leadLists[0]?.signature || null;

                // Wenn die ursprüngliche Eigenrecherche-Liste noch existiert, dorthin zurück.
                if (hasRequestedResearchList) {
                    return {
                        name: 'LeadGeneration',
                        query: { signature: requestedSignature, resumeConversionPopup: 'true' },
                        hash: '#' + businessPartnerGpNo
                    };
                }

                // Falls keine Signatur in der Route steht, aber es noch andere Suchen gibt,
                // zur nächsten verfügbaren Eigenrecherche-Liste wechseln.
                if (fallbackResearchSignature) {
                    return {
                        name: 'LeadGeneration',
                        query: { signature: fallbackResearchSignature, resumeConversionPopup: 'true' },
                        hash: '#' + businessPartnerGpNo
                    };
                }

                // Die ursprüngliche Liste wurde gelöscht und es gibt keine weitere Suche mehr:
                // zurück zur Kundenliste mit aktivem Filterkontext statt in eine leere Recherche-Ansicht.
                return {
                    path: '/businessPartnerList',
                    hash: businessPartnerGpNo ? ('#' + businessPartnerGpNo) : undefined,
                    query: {
                        ...query,
                    }
                };
            } else if (directSingleFromSearch && routeSearchMode === 'GPNo') {
                // Früheres Verhalten: GPNo Einzel-Suche zurück auf Dashboard (Potential & BK Abschnitt)
                return { name: 'Dashboard', hash: '#potential-bk' };
            } else if (directSingleFromSearch) {
                // Allgemeine Einzel-Suche (ohne Liste): zurück zur BusinessPartnerList mit Suchkontext
                return {
                    name: 'BusinessPartnerList',
                    hash: businessPartnerGpNo ? ('#' + businessPartnerGpNo) : undefined,
                    query: {
                        source: 'search',
                        searchMode: routeSearchMode,
                        search: routeSearchQuery,
                        wholeWord: userStore.currentSelection.businessPartnerList.search.wholeWord ? 1 : 0
                    }
                };
            } else {
                // Fallback: Explizit Dashboard statt history.go(-1) (vermeidet weißen Screen bei leerem History/abgebrochenem Guard)
                return { name: 'Dashboard' };
            }
        }
    }

    const backNavigationTarget = computed(() => getBackNavigationTarget());

    async function navigateBack() {
        try { isNavigatingBack.value = true; } catch(e) { /* ignore */ }
        try {
            await router.push(getBackNavigationTarget());
        } finally {
            isNavigatingBack.value = false;
        }
    }

    function handleEditContactInfo() {
        // opens the edit mode for the contact information
        userStore.currentSelection.businessPartner.editMode = !userStore.currentSelection.businessPartner.editMode;
    }
        // ...existing imports...
    const bpInitial = businessPartnersStore.businessPartner;
    const contacts = ref(
        Array.isArray(bpInitial?.contacts)
            ? [...bpInitial.contacts]
            : (bpInitial?.contacts ? [bpInitial.contacts] : [])
        );
    const currentContactIndex = ref(0);
    const initializedBusinessPartnerId = ref(null);
    // Currently selected Entscheider (by contact id) for sales pitch actions
    const selectedContactId = ref('');
    const UNKNOWN_DECISION_MAKER_ID = '__unknown__';
    // Sales pitch flow state
    const currentStatusSection = ref('');
    const currentSelectedOption = ref('');
    const currentComment = ref('');
    const newProductForAppointment = ref(-1);
    onMounted(() => { newProductForAppointment.value = -1; });
    const decisionMakerInvalid = ref(false);
    const immediateCloseEnabled = ref(false);
    const immediateCloseLoading = ref(false);
    // Helper: derive primary email across possible shapes
    function primaryEmail(contact){
        if(!contact) return '';
        const direct = (contact.email || '').trim();
        if (direct) return direct;
        if (Array.isArray(contact.emailAddresses) && contact.emailAddresses[0]) return String(contact.emailAddresses[0]).trim();
        if (Array.isArray(contact.emailList) && contact.emailList[0]?.name) return String(contact.emailList[0].name).trim();
        return '';
    }
    function isUnknownDecisionMakerSelected() {
        return String(selectedContactId.value) === UNKNOWN_DECISION_MAKER_ID;
    }
    // Preselect first valid Entscheider with a valid email
    function preselectValidDecisionMaker(){
        if (!contacts.value || contacts.value.length === 0) return;
        // keep current valid selection
        if (selectedContactId.value){
            if (isUnknownDecisionMakerSelected()) return;
            const cur = contacts.value.find(c => String(c.id) === String(selectedContactId.value));
            if (cur && isValidEmail(primaryEmail(cur))) return;
        }
        const firstValid = contacts.value.find(c => isValidEmail(primaryEmail(c)));
        if (firstValid) selectedContactId.value = firstValid.id;
    }
    function getMainContactIndex(list) {
        if (!Array.isArray(list) || list.length === 0) return -1;
        const mainIndex = list.findIndex(contact => contact?.main === true);
        return mainIndex >= 0 ? mainIndex : 0;
    }
    function applyInitialContactIndex(bp, list) {
        if (!Array.isArray(list) || list.length === 0) {
            currentContactIndex.value = -1;
            return;
        }

        const bpId = bp?.id ?? null;
        const isNewBusinessPartner = initializedBusinessPartnerId.value !== bpId;

        if (isNewBusinessPartner) {
            currentContactIndex.value = getMainContactIndex(list);
            initializedBusinessPartnerId.value = bpId;
            return;
        }

        if (currentContactIndex.value < 0 || currentContactIndex.value >= list.length) {
            currentContactIndex.value = 0;
        }
    }
    // Aktualisiere Kontakte, sobald der BusinessPartner im Store geladen/verfügbar ist
    watch(() => businessPartnersStore.businessPartner, (bp) => {
        const list = Array.isArray(bp?.contacts)
            ? [...bp.contacts]
            : (bp?.contacts ? [bp.contacts] : []);
        contacts.value = list;
        applyInitialContactIndex(bp, list);
        preselectValidDecisionMaker();
    });
    onMounted(() => {
        applyInitialContactIndex(bpInitial, contacts.value);
        preselectValidDecisionMaker();
    });
    // Potential lists derived from offerList
    // Reaktive Kopie der offerList
    const offerListCopy = ref([]);
    function syncOfferList() {
        const bp = getBusinessPartnerWithSalesPitch();
        if (!bp) {
            offerListCopy.value = [];
            return;
        }
        offerListCopy.value = JSON.parse(JSON.stringify(bp.salesPitch.offerList));
    }
    // Initiales Sync
    syncOfferList();
    // Sync bei Store-Änderung
    watch(() => businessPartnersStore.businessPartner?.salesPitch?.offerList, syncOfferList, { deep: true });

    function getOfferStatusId(offer) {
        return Number(offer?.status?.id ?? -1);
    }

    const offersWithFeedback = computed(() => {
        return offerListCopy.value.filter((offer) => getOfferStatusId(offer) !== 16);
    });

    const offersForSalesPitch = computed(() => {
        return offerListCopy.value.filter((offer) => getOfferStatusId(offer) === 16);
    });

    const availablePotentialProducts = computed(() => {
        const selected = new Set(offerListCopy.value.map(o => normalizeProductId(o?.product?.id)));
        const all = userStore.filterOption?.businessPartnerPotential || [];
        return all.filter(p => !selected.has(normalizeProductId(p.id)));
    });
    const warning = ref(false);

    const currentContact = computed(() => {
        // Wenn kein Kontakt existiert, gib ein leeres Kontakt-Objekt zurück
        if (
            !contacts.value ||
            contacts.value.length === 0 ||
            currentContactIndex.value < 0 ||
            currentContactIndex.value >= contacts.value.length
        ) {
            return {
                gender: null,
                firstName: '',
                lastName: '',
                phoneStationary: '',
                phoneMobile: '',
                email: ''
            };
        }
        return contacts.value[currentContactIndex.value];
    });    
    
    function nextContact() {
      if (currentContactIndex.value < contacts.value.length - 1) currentContactIndex.value++;
    }
    function prevContact() {
      if (currentContactIndex.value > 0) currentContactIndex.value--;
    }

    async function handleCloseEditContactInfo() {
        // Kontakt-Daten für API vorbereiten
        const contactData = {
            id: currentContact.value.id || null,
            idBusinessPartner: currentContact.value.idBusinessPartner || businessPartnersStore.businessPartner?.id || null,
            gender: currentContact.value.gender || null,
            firstName: currentContact.value.firstName || null,
            lastName: currentContact.value.lastName || null,
            phoneStationary: currentContact.value.phoneStationary || '',
            phoneMobile: currentContact.value.phoneMobile || '',
            email: currentContact.value.email || ''
        };

        userStore.currentSelection.businessPartner.contacts.isLoading = true;
        let response = null;
        if (contactData.id) {
            // Update bestehender Kontakt
            response = await businessPartnersStore.updateContact(contactData);
            const prev = contacts.value[currentContactIndex.value];
            if (response?.businessPartnerContact) {
                const merged = { ...prev, ...response.businessPartnerContact };
                // Falls Backend veraltete E-Mail zurückliefert (Race / Delay), erzwinge neue Eingabe
                if (contactData.email && contactData.email.trim() !== '' && contactData.email !== merged.email) {
                    merged.email = contactData.email;
                    merged.emailList = [{ name: contactData.email }];
                    merged.emailAddresses = [contactData.email];
                }
                contacts.value[currentContactIndex.value] = merged;
            } else {
                // Optimistic fallback wenn keine Response-Daten
                const optimistic = { ...prev };
                if (contactData.email && contactData.email.trim() !== '') {
                    optimistic.email = contactData.email;
                    optimistic.emailList = [{ name: contactData.email }];
                    optimistic.emailAddresses = [contactData.email];
                }
                contacts.value[currentContactIndex.value] = optimistic;
            }
        } else {
            // Neuer Kontakt
            
            response = await businessPartnersStore.saveContact(contactData);
            // If response is the contact object itself, merge directly
            if (response && typeof response === 'object') {
                const merged = { ...contacts.value[currentContactIndex.value], ...response };
                // Ensure merged.sugar always exists if sugar.id is present in response
                if (response.sugar && response.sugar.id) {
                    merged.sugar = merged.sugar || {};
                    merged.sugar.id = response.sugar.id;
                } else if (response.businessPartner && response.businessPartner.sugar && response.businessPartner.sugar.id) {
                    merged.sugar = merged.sugar || {};
                    merged.sugar.id = response.businessPartner.sugar.id;
                }
                if (contactData.email && contactData.email.trim() !== '') {
                    merged.email = contactData.email; // sicherstellen
                    merged.emailList = [{ name: contactData.email }];
                    merged.emailAddresses = [contactData.email];
                }
                contacts.value[currentContactIndex.value] = merged;
            }
        }
        // Email Normalisierung sofort anwenden, damit kein Reload nötig ist
        const updated = contacts.value[currentContactIndex.value];
        if (response?.businessPartnerContact?.emailAddresses?.length) {
            updated.emailList = response.businessPartnerContact.emailAddresses.map(e => ({ name: typeof e === 'string' ? e : (e?.name || '') }));
            updated.email = updated.emailList[0].name;
        } else if (updated.email && (!updated.emailList || updated.emailList.length === 0)) {
            updated.emailList = [{ name: updated.email }];
        } else if (!updated.email && (!updated.emailList || updated.emailList.length === 0)) {
            updated.emailList = [{ name: '-- unbekannt --' }];
            updated.email = updated.emailList[0].name;
        }
        // Setze main:true für neuen Kontakt und main:false für alle anderen (nur ein Haupt-Entscheider)
        contacts.value.forEach((c, idx) => {
            c.main = (idx === currentContactIndex.value);
        });
        // Setze dateCreated, falls nicht vorhanden
        if (!updated.dateCreated) {
            updated.dateCreated = new Date().toISOString();
        }
        // Telefonnummern IMMER auf die zuletzt eingegebenen Werte setzen (Formular hat Vorrang)
        updated.phoneMobile = contactData.phoneMobile;
        updated.phoneStationary = contactData.phoneStationary;
        if (businessPartnersStore.businessPartner) {
            businessPartnersStore.businessPartner.contacts = [...contacts.value];
        }
        userStore.currentSelection.businessPartner.contacts.isLoading = false;
        userStore.currentSelection.businessPartner.editMode = false;
        checkSalesPitchIsValid();
    }
    
    function handleUndoEditContactInfo() {
            // Wenn der aktuelle Kontakt keinen Backend-Identifier (id) hat, wurde er noch nicht gespeichert → entfernen
            if (
                contacts.value.length > 0 &&
                currentContactIndex.value >= 0 &&
                currentContactIndex.value < contacts.value.length &&
                !contacts.value[currentContactIndex.value].id
            ) {
                contacts.value.splice(currentContactIndex.value, 1);
                // Index anpassen: auf vorherigen Kontakt oder -1, falls leer
                if (contacts.value.length === 0) {
                    currentContactIndex.value = -1;
                } else if (currentContactIndex.value > 0) {
                    currentContactIndex.value--;
                } else {
                    currentContactIndex.value = 0;
                }
            } else if (contacts.value.length > 0 && currentContactIndex.value >= 0 && currentContactIndex.value < contacts.value.length) {
                // Andernfalls Änderungen am bestehenden Entscheider zurücksetzen
                const bpContacts = businessPartnersStore.businessPartner?.contacts;
                if (Array.isArray(bpContacts) && bpContacts[currentContactIndex.value]) {
                    contacts.value[currentContactIndex.value] = { ...bpContacts[currentContactIndex.value] };
                }
            }
            // Wenn keine Kontakte mehr vorhanden sind, currentContactIndex auf -1 setzen, damit kein Kontakt angezeigt wird
            if (!contacts.value || contacts.value.length === 0) {
                currentContactIndex.value = -1;
            }
            userStore.currentSelection.businessPartner.editMode = false;
    }

    function addContact() {
    contacts.value.push({
                gender: null,
                firstName: '',
                lastName: '',
                phoneStationary: '',
                phoneMobile: '',
                email: '',
                sugar: { id: null}
            });
            if (businessPartnersStore.businessPartner) {
                businessPartnersStore.businessPartner.contacts = [...contacts.value];
            }
            currentContactIndex.value = contacts.value.length - 1;
            userStore.currentSelection.businessPartner.editMode = true;
    }

    function formatDateTime(dateTimeString) {
        const date = new Date(dateTimeString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Monate sind nullbasiert
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${day}.${month}.${year} ${hours}:${minutes}`;
    }

    function copyToClipboard(text) {
        if(!text || typeof text !== 'string' || text.trim() === '') return Promise.resolve(false);
        const value = text.trim();
        return navigator.clipboard.writeText(value)
            .then(() => true)
            .catch(err => { console.error('Clipboard copy failed:', err); return false; });
    }

    function handleLinkClick(event, text) {
        // Kopier-Logik:
        // - Wenn ein expliziter Text-Parameter übergeben wurde: genau diesen kopieren (nicht als URL verändern)
        // - Sonst Fallback: href / data-copy / sichtbarer Text
        // - Nur wenn der Fallback wie eine URL aussieht (keine Leerzeichen, enthält Punkt oder startet mit http) ggf. https:// ergänzen.
        const target = event?.currentTarget || event?.target;
        const explicit = (typeof text === 'string' && text.trim() !== '');
        let value = explicit ? text.trim() : (
            target?.getAttribute?.('data-copy')
            || target?.getAttribute?.('href')
            || target?.textContent
            || ''
        );
        const isLikelyUrl = (str) => !!str && !str.includes(' ') && (str.startsWith('http://') || str.startsWith('https://') || str.includes('.'));
        if(!explicit && isLikelyUrl(value)) {
            // Nur Fallback-Links normalisieren
            value = formatWebsiteLink(value);
        }
        copyToClipboard(value);
    }

    function formatWebsiteLink(link) {
        if (!link) return '#';
        // Überprüfen, ob der Link mit http:// oder https:// beginnt
        if (!link.startsWith('http://') && !link.startsWith('https://')) {
            return `https://${link}`; // Füge https:// hinzu, wenn es fehlt
        }
        return link; // Gib den Link unverändert zurück, wenn er korrekt ist
    }   
    function copyInputValue(event) {
        const button = event.target.closest('button'); // Button, der geklickt wurde
        const input = button?.parentElement.querySelector('input'); // Sucht das zugehörige Input-Feld
    
        if (input && input.value) {
            navigator.clipboard.writeText(input.value)
                .then(() => {
                    // Feedback anzeigen
                    const feedback = document.createElement('span');
                    feedback.textContent = 'Kopiert!';
                    feedback.className = 'copy-feedback success';
                    button.parentElement.appendChild(feedback);
    
                    // Feedback nach 2 Sekunden entfernen
                    setTimeout(() => {
                        feedback.remove();
                    }, 2000);
                })
                .catch(err => {
                    console.error('Failed to copy text:', err);
                });
        } else {
            console.warn('No input value to copy.');
            const feedback = document.createElement('span');
            feedback.textContent = 'Keinen Inhalt zum kopieren!';
            feedback.className = 'copy-feedback failure';
            button.parentElement.appendChild(feedback);

            // Feedback nach 2 Sekunden entfernen
            setTimeout(() => {
                feedback.remove();
            }, 2000);
        }
    }

    const handleSelectList = (idList) => {
        // console.log('routing to list:', idList);
        // set the page to 1 to avoid pagination issues
        userStore.currentSelection.businessPartnerList.page = 1;
        userStore.currentSelection.businessPartnerList.pageSize = 24;
        userStore.currentSelection.businessPartnerList.filter.idBusinessPartnerList = idList;
        router.push({ name: 'BusinessPartnerList', query: { source: 'filter', filter: `idBusinessPartnerStatus:-1|idBusinessPartnerAboStatus:-1|idBusinessPartnerList:${idList}|idBusinessPartnerProduct:-1|idBusinessPartnerPotential:-1|idBusinessPartnerCityZip:-1|nameBusinessPartnerSector:|idBusinessPartnerOrder:-1` }});
    };

    // Flags für Anforderungen (werden in checkSalesPitchIsValid gesetzt)
    const decisionMakerNeeded = ref(false);
    const potentialNeeded = ref(false);
    const dateNeeded = ref(false);
    const timeNeeded = ref(false);

    

    const saveRequirementsMessage = computed(()=> {
        const reasons = saveBlockingReasons.value || [];
        if (!reasons.length) return 'Alle Voraussetzungen erfüllt.';
        return 'Fehlt: ' + reasons.join(', ');
    });

    const isSaveDisabled = computed(() => {
        if (saveSalesPitchIsLoading.value) return true;
        if (userStore.preventSave) return true;
        if (userStore.currentSelection.businessPartner.isLoading === true) return true;
        if (!userStore.currentSelection.businessPartner?.hasValid?.salesPitch) return true;
        if (status.currentStep === 2 && decisionMakerNeeded.value && decisionMakerInvalid.value) return true;
        return false;
    });

    // Gründe warum Speichern (noch) nicht möglich ist
    const saveBlockingReasons = computed(() => {
        const reasons = [];
        // Generisch
        if (saveSalesPitchIsLoading.value) reasons.push('Speichervorgang läuft...');
        if (userStore.preventSave) reasons.push('Speichern ist aktuell gesperrt.');
        if (userStore.currentSelection.businessPartner.isLoading === true) reasons.push('Daten werden noch geladen.');
        const invalidUngültig = currentStatusSection.value === 'Entscheider Kontakt hergestellt' && currentSelectedOption.value?.startsWith?.('Ungültig:');
        if (invalidUngültig) return reasons; // Ungültig braucht nichts weiter
        // Detailanforderungen
        if (status.currentStep === 2 && decisionMakerNeeded.value && decisionMakerInvalid.value) reasons.push('Gültigen Entscheider wählen.');
        if (currentStatusSection.value === 'Unverbindliche Wiedervorlage' && currentSelectedOption.value==='Datum eintragen') {
            if (!status.datePicker['unverbindlich'].selected.date) reasons.push('Wiedervorlage-Datum wählen.');
            // Kein Potential notwendig für unverbindliche Wiedervorlage
        }
        if (currentStatusSection.value === 'Verbindlicher Termin mit Entscheider' && ['Rückruf','Termin vor Ort'].includes(currentSelectedOption.value)) {
            if (!status.datePicker['verbindlich'].selected.date) reasons.push('Termin-Datum wählen.');
            if (!status.datePicker['verbindlich'].selected.time) reasons.push('Termin-Uhrzeit wählen.');
            if (offersForSalesPitch.value.length === 0) reasons.push('Mind. ein Potential auswählen.');
        }
        return reasons;
    });

    // Save Button anzeigen:
    //  - Schritt 1: bei Ungültig-Auswahl (Dublette / kein Anschluss) immer sofort (auch wenn gerade disabled -> dann Gründe unten)
    //  - Schritt 2: nur wenn speicherbar (valid)
    const showSaveButton = computed(() => {
        if (status.currentStep === 1) {
            const invalidSelected = currentStatusSection.value === 'Entscheider Kontakt hergestellt' && currentSelectedOption.value?.startsWith?.('Ungültig:');
            return invalidSelected;
        }
        if (status.currentStep === 2) return !isSaveDisabled.value;
        return false;
    });

    // Sichtbarkeit (nicht Enable) für Sofort Abschluss
    const showImmediateCloseButton = computed(() => (
        status.currentStep === 1 &&
        currentStatusSection.value === 'Entscheider Kontakt hergestellt' &&
        ['Ja, telefonisch','Ja, vor Ort'].includes(currentSelectedOption.value)
    ));
    const showNextButton = computed(() => {
        // Schritt 1: Weiter-Button immer anzeigen (für klare Navigation), außer Ungültig-Pfad (dort nur Speichern nach Auswahl)
        if (status.currentStep !== 1) return false;
        const invalidSelected = currentStatusSection.value === 'Entscheider Kontakt hergestellt' && currentSelectedOption.value?.startsWith?.('Ungültig:');
        if (invalidSelected) return false;
        return true; // Aktivierung/Deaktivierung steuert ausschließlich :disabled über status.allowNext
    });

    // Re-Validate automatically when user selects / changes Entscheider
    watch(selectedContactId, () => {
        // Nur prüfen, wenn ein Context besteht, sonst früh raus
        if (!currentStatusSection.value) return;
        if (decisionMakerNeeded.value){
            if (!selectedContactId.value || selectedContactId.value === ''){
                decisionMakerInvalid.value = true;
                userStore.currentSelection.businessPartner.hasValid.salesPitch = false;
            } else {
                const chosen = contacts.value.find(c => String(c.id) === String(selectedContactId.value));
                const candidateEmail = primaryEmail(chosen);
                decisionMakerInvalid.value = !isValidEmail(candidateEmail);
            }
        }
        checkSalesPitchIsValid();
    });
    // If context starts requiring a decision maker and none selected, try to preselect
    watch([currentStatusSection, currentSelectedOption], () => {
        if (decisionMakerNeeded.value && (!selectedContactId.value || selectedContactId.value === '')) {
            preselectValidDecisionMaker();
        }
    });

    // Lazy load state for Products360 and CasesTicket
</script>

<template>
    <div class="reminder-wrapper singlepage-wrapper max-width">
        <main id="container-main">
                <div
                    v-if="isNavigatingBack || saveSalesPitchIsLoading || isSwitchingBusinessPartner || isOpeningNextCustomer"
                    class="list-reloading-overlay"
                >
                    <div class="list-reloading-inner">
                        <InlineSpinner size="lg" />
                        <div class="list-reloading-text">
                            {{ isNavigatingBack ? 'Zurück zur Liste...' : (isOpeningNextCustomer ? 'Nächster Kunde wird geöffnet…' : (saveSalesPitchIsLoading ? 'Kunde wird gespeichert…' : 'Kunde wird geöffnet…')) }}
                        </div>
                    </div>
                </div>
                <div @click.capture="handlePreviewHeaderBackClick">
                    <PreviewHeader
                        title="GP Details"
                        :to="backNavigationTarget"
                        button-label="Zurück zur Liste"
                        arrow="left"
                    />
                </div>
                <div v-if="canNavigateWithinList" class="bp-list-navigation">
                    <button
                        type="button"
                        class="bp-list-navigation-btn"
                        :disabled="!hasPreviousBusinessPartner || isSwitchingBusinessPartner"
                        @click="navigateRelativeInList(-1)"
                    >
                        <span class="bp-list-navigation-header">
                            <span class="bp-list-navigation-title">← Zurück ({{ previousPositionLabel }})</span>
                            <span v-if="Number(previousBusinessPartner?.unseen || 0) > 0" class="bp-list-navigation-unseen">Ungesehen</span>
                        </span>
                        <span class="bp-list-navigation-name">{{ truncateNavLabel(navigationDisplayName(previousBusinessPartner)) }}</span>
                        <span class="bp-list-navigation-meta">{{ navigationMetaLabel(previousBusinessPartner) }}</span>
                    </button>
                    <button
                        type="button"
                        class="bp-list-navigation-btn"
                        :disabled="!hasNextBusinessPartner || isSwitchingBusinessPartner"
                        @click="navigateRelativeInList(1)"
                    >
                        <span class="bp-list-navigation-header">
                            <span class="bp-list-navigation-title">Weiter → ({{ nextPositionLabel }})</span>
                            <span v-if="Number(nextBusinessPartner?.unseen || 0) > 0" class="bp-list-navigation-unseen">Ungesehen</span>
                        </span>
                        <span class="bp-list-navigation-name">{{ truncateNavLabel(navigationDisplayName(nextBusinessPartner)) }}</span>
                        <span class="bp-list-navigation-meta">{{ navigationMetaLabel(nextBusinessPartner) }}</span>
                    </button>
                </div>
                <div>
                    <div class="widget-wrapper potential-bk customer-details" :style="{ maxHeight: maxWidgetHeight }">
                        <!-- <div class="bp-detail-header">
                            <template v-if="userStore.isManagement || userStore.isSalesTeamManager || userStore.isWinback">
                                <div class="userAvatar">
                                    <userAvatar v-if="businessPartnersStore.businessPartner" :size="48" :showSalesAgent="true" :isSalesAgent="!userStore.isManagement && !userStore.isSalesTeamManager && !userStore.isWinback" :salesAgentId="businessPartnersStore.businessPartner?.idSalesAgent || null" />
                                    <div v-else class="bp-avatar-skeleton" aria-hidden="true"></div>
                                </div>
                            </template>
                        </div> -->
                        <div class="container-wrapper">
                            <div class="flexWrapper grid">
                                <!-- <div :class="['container creditWorthiness', {'worthiness' : businessPartnersStore.businessPartner.creditWorthiness.symbol === 'OK'}]">
                                    <h3 :style="{color: businessPartnersStore.businessPartner.creditWorthiness.color}">{{ businessPartnersStore.businessPartner.creditWorthiness.description }}</h3>
                                </div> -->
                                <div class="column">
                                    <div :class="['accordion-menu customer-note', {'deactivated' : businessPartnersStore.businessPartner?.info}]" v-if="businessPartnersStore.businessPartner?.info">
                                            <ul class="main-list">
                                                <li class="main-list-item">
                                                    <input type="checkbox">
                                                    <i class="arrow"></i>
                                                    <h3>Hinweise</h3>
                                                    <div class="accordion-content-wrapper">
                                                        <div class="notice-details-content">
                                                            <p v-if="businessPartnersStore.businessPartner.info">{{ businessPartnersStore.businessPartner.info }}</p>
                                                            <p v-else>Keine Hinweise vorhanden</p>
                                                        </div>
                                                    </div>
                                                </li>
                                            </ul>
                                    </div>
                                    <div :class="['accordion-menu customer-details', {'preventOnDesktop' : userStore.selectedMedia === 'desktop'}]">
                                        <ul class="main-list">
                                            <li class="main-list-item">
                                                <input type="checkbox" checked>
                                                <i class="arrow"></i>
                                                <h3>Kundendaten <span v-if="contactNeedsUpdate">(!)</span></h3>
                                                <div class="accordion-content-wrapper">
                                                    <CustomerDetails
                                                        :currentContact="currentContact"
                                                        :contacts="contacts"
                                                        :currentContactIndex="currentContactIndex"
                                                        :editMode="userStore.currentSelection.businessPartner.editMode"
                                                        :onPrev="prevContact"
                                                        :onNext="nextContact"
                                                        :onAdd="addContact"
                                                        :onEdit="handleEditContactInfo"
                                                        :onSave="handleCloseEditContactInfo"
                                                        :onUndo="handleUndoEditContactInfo"
                                                        :copyInputValue="copyInputValue"
                                                    >
                                                        <template v-slot:decision-maker>
                                                            <div :id="'section-decision-maker'">
                                                                <DecisionMaker
                                                                    :currentContact="currentContact"
                                                                    :contacts="contacts"
                                                                    :currentContactIndex="currentContactIndex"
                                                                    :editMode="userStore.currentSelection.businessPartner.editMode"
                                                                    :onPrev="prevContact"
                                                                    :onNext="nextContact"
                                                                    :onAdd="addContact"
                                                                    :onEdit="handleEditContactInfo"
                                                                    :onSave="handleCloseEditContactInfo"
                                                                    :onUndo="handleUndoEditContactInfo"
                                                                    :copyInputValue="copyInputValue"
                                                                />
                                                            </div>
                                                        </template>
                                                    </CustomerDetails>
                                                    <div class="container main-content toggle-me cell-grid" v-if="userStore.currentSelection.businessPartner.contacts.isLoading">
                                                        <div class="loader" id="loader"></div>
                                                        <div class="loadingDescription">Daten werden gespeichert...</div>
                                                    </div>
                                                    
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <div class="column">
                                    <div :id="'section-status'">
                                    <div id="section-dates" aria-hidden="true"></div>
                                    <SalesPitchStatus
                                        :key="`${String(businessPartnersStore.businessPartner?.gpNo || route.query.search || '')}-${salesPitchStatusRenderKey}`"
                                        :status="status"
                                        :campaignActive="new Date() <= new Date(userStore.currentSelection.campaign.dateEnd)"
                                        :currentComment="currentComment"
                                        :onUpdateComment="(val) => { currentComment.value = val }"
                                        :contacts="contacts"
                                        :decisionMakerNeeded="decisionMakerNeeded"
                                        :decisionMakerInvalid="decisionMakerInvalid"
                                        :selectedContactId="selectedContactId"
                                        :onUpdateSelectedContactId="(val) => {
                                            if (selectedContactId && typeof selectedContactId === 'object' && 'value' in selectedContactId) {
                                                selectedContactId.value = val
                                            } else {
                                                // fallback: assign directly (should not happen)
                                                selectedContactId = val
                                            }
                                        }"
                                        :isValidEmail="isValidEmail"
                                        :immediateCloseEnabled="immediateCloseEnabled"
                                        :immediateCloseLoading="immediateCloseLoading"
                                        :handleImmediateClose="handleImmediateClose"
                                        :showNextButton="showNextButton"
                                        :showSaveButton="showSaveButton"
                                        :isSaveDisabled="isSaveDisabled"
                                        :handleGoToPreviousStep="handleGoToPreviousStep"
                                        :handleGoToNextStep="handleGoToNextStep"
                                        :handleSaveSalesPitch="handleSaveSalesPitch"
                                        :saveSalesPitchIsLoading="saveSalesPitchIsLoading"
                                        :saveRequirementsMessage="saveRequirementsMessage"
                                        :saveBlockingReasons="saveBlockingReasons"
                                        :handleChangeStatus="handleChangeStatus"
                                        :sourceSugar="businessPartnersStore.businessPartner?.sourceSugar"
                                        :gpNo="businessPartnersStore.businessPartner?.gpNo"
                                        :idSugar="businessPartnersStore.businessPartner?.idSugar"
                                    />
                                    </div>
                                    <div :id="'section-potential'">
                                    <PotentialSection
                                        :isDesktop="userStore.selectedMedia === 'desktop'"
                                        :offersWithFeedback="offersWithFeedback"
                                        :offersForSalesPitch="offersForSalesPitch"
                                        :availablePotentialProducts="availablePotentialProducts"
                                        :newProductForAppointment="newProductForAppointment"
                                        :offerList="offerListCopy"
                                        :warning="warning"
                                        :onAddProduct="handleAddProduct"
                                        :onRemoveProduct="handleRemoveProduct"
                                        :onUpdateNewProductForAppointment="handleUpdateNewProductForAppointment"
                                    />
                                    </div>

                                </div>
                                <div class="column">
                                    <SalesPitchHistory :salesPitchList="businessPartnersStore.businessPartner?.salesPitchList || []" :isDesktop="userStore.selectedMedia === 'desktop'" />
                                    <!-- Ursprüngliche Verzeichnisse Tabelle entfernt (jetzt unter Listen positioniert) -->
                                </div>
                            </div>
                            <div>
                                <Suspense>
                                    <template #default>
                                        <CasesTicket
                                            :idUser="userStore.id"
                                            :idSugar="businessPartnersStore.businessPartner?.idSugar"
                                            :sugarTickets="businessPartnersStore.businessPartner?.sugarTickets"
                                        />
                                    </template>
                                    <template #fallback>
                                        <Spinner />
                                    </template>
                                </Suspense>
                            </div>
                            <div>
                                <!-- TEMPORÄR DEAKTIVIERT: Feature "Verwandte Kunden" (siehe RelatedBusinessPartners Import oben)
                                <Suspense>
                                    <template #default>
                                        <RelatedBusinessPartners
                                            :idUser="userStore.id"
                                            :idSugar="businessPartnersStore.businessPartner?.idSugar"
                                        />
                                    </template>
                                    <template #fallback>
                                        <Spinner />
                                    </template>
                                </Suspense>
                                -->
                            </div>
                            <div>
                                <Suspense>
                                    <template #default>
                                        <Products360
                                            :isDesktop="userStore.selectedMedia === 'desktop'"
                                            :anyProductsForSignal="anyProductsForSignal"
                                            :groupsWithAnyProducts="groupsWithAnyProducts"
                                            :productsForSignalAndGroup="productsForSignalAndGroup"
                                            :groupedContractsForProduct="groupedContractsForProduct"
                                            :isProductOpen="isProductOpen"
                                            :toggleProduct="toggleProduct"
                                            :getDocumentsForContract="getDocumentsForContract"
                                            :isContractDocsOpen="isContractDocsOpen"
                                            :toggleContractDocs="toggleContractDocs"
                                            :handleDocumentClick="handleDocumentClick"
                                            :formatGermanDate="formatGermanDate"
                                            :formatDocDate="formatDocDate"
                                            :folderOpenIcon="folderOpenIcon"
                                            :folderClosedIcon="folderClosedIcon"
                                        />
                                    </template>
                                    <template #fallback>
                                        <Spinner />
                                    </template>
                                </Suspense>
                            </div>
                        </div>
                        <div class='wave-wrapper'>
                            <div class='wave -one'></div>
                            <div class='wave -two'></div>
                            <div class='wave -three'></div>
                        </div>
                    </div>
                    <!-- <div class="operation-wrapper">
                        <p>{{ incompleteSalesPitchMessage }}</p>
                        <div class="buttons">
                            <button :class="['save', {'disabled cell-grid': saveSalesPitchIsLoading || userStore.preventSave}]" :disabled="saveSalesPitchIsLoading || userStore.preventSave || userStore.currentSelection.businessPartner.isLoading === true || !userStore.currentSelection.businessPartner?.hasValid?.salesPitch || ((Object.values(status['Verbindlicher Termin mit Entscheider'].options).some(option => option.checked === true) && offersForSalesPitch.length === 0))" @click="handleSaveSalesPitch()">
                                Speichern
                                <div v-if="saveSalesPitchIsLoading" class="loader"></div>
                            </button> 
                            <button id="abortEdit" @click="handleAbortSalesPitch()" name="abortEditContact">Abbrechen</button>
                        </div>
                    </div> -->
                    <reminder></reminder>
                </div>
            </main>
        </div>
</template>
<style>
.bp-detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.7rem;
    flex-wrap: wrap;
}

.bp-list-navigation {
    display: inline-flex;
    align-items: center;
    gap: 0.45rem;
}

@media (max-width: 640px) {
    .bp-list-navigation {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        width: 100%;
        gap: 0.45rem;
    }
    .bp-list-navigation-btn {
        flex: 1 1 0;
        min-width: 0;
        max-width: none;
        overflow: hidden;
    }
    .bp-list-navigation-title {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
    }
    .bp-list-navigation-unseen {
        flex-shrink: 0;
    }
    .bp-list-navigation-header {
        overflow: hidden;
    }
}
.bp-list-navigation-btn {
    border: 1px solid #cbd5e1;
    background: #f8fafc;
    color: #334155;
    border-radius: 8px;
    padding: 0.32rem 0.68rem;
    font-size: 0.62rem;
    font-weight: 600;
    cursor: pointer;
    min-width: 100px;
    max-width: 230px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.14rem;
    text-align: left;
}

.bp-list-navigation-header {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.4rem;
}

.bp-list-navigation-title {
    font-size: 0.62rem;
    line-height: 1;
}

.bp-list-navigation-name {
    font-size: 0.64rem;
    font-weight: 700;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
}

.bp-list-navigation-meta {
    font-size: 0.52rem;
    line-height: 1;
    color: #64748b;
}

.bp-list-navigation-unseen {
    font-size: 0.52rem;
    font-weight: 700;
    line-height: 1;
    color: #b91c1c;
    background: #fee2e2;
    border: 1px solid #fecaca;
    border-radius: 999px;
    padding: 0.11rem 0.36rem;
}

.bp-list-navigation-btn:hover:not(:disabled) {
    background: #e2e8f0;
}

.bp-list-navigation-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}

.list-reloading-overlay {
    position: fixed;
    inset: 0;
    z-index: 1500;
    background: rgba(255, 255, 255, 0.78);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: all;
}

.list-reloading-inner {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.85rem;
    padding: 1.2rem 1.8rem;
    border-radius: 1rem;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.18);
    border: 1px solid rgba(148, 163, 184, 0.6);
}

.list-reloading-text {
    font-size: 0.9rem;
    font-weight: 600;
    color: #111827;
}


.google-sellwerk-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 12px;
        flex-wrap: wrap;
}
.google-sellwerk-row .sellwerk-manager-logo {
    width: max-content;
}
@media (max-width: 640px) {
    .google-sellwerk-row {
        flex-direction: column;
    }
}
.products-details-content .signal-icon { width:32px; height:32px; max-height:32px; object-fit:contain; display:inline-block; }
.products-details-content .empty-hint { font-size:.75rem; font-style:italic; opacity:.7; margin:.35rem 0 1rem 0; }
/* Produktgruppen Container Hintergründe */
.products-details-content .productGroup-wrapper .container { border-radius:10px; padding:.55rem .75rem .6rem; margin:0 0 1rem 0; position:relative; box-shadow:inset 0 0 0 1px rgba(255,255,255,.05),0 2px 4px -2px rgba(0,0,0,.5); }
.theme--light .products-details-content .productGroup-wrapper .container { box-shadow:inset 0 0 0 1px rgba(0,0,0,.07),0 2px 4px -2px rgba(0,0,0,.18); }
.products-details-content .productGroup-wrapper .container.GS { background:linear-gradient(135deg,rgba(248,217,74,.45),rgba(245,196,0,.55)); }
.theme--light .products-details-content .productGroup-wrapper .container.GS { background:linear-gradient(135deg,rgba(255,224,102,.55),rgba(255,210,58,.6)); }
.products-details-content .productGroup-wrapper .container.OE { background:linear-gradient(135deg,rgba(29,78,216,.38),rgba(37,99,235,.46)); }
.theme--light .products-details-content .productGroup-wrapper .container.OE { background:linear-gradient(135deg,rgba(59,130,246,.4),rgba(37,99,235,.45)); }
.products-details-content .productGroup-wrapper .container.TB { background:linear-gradient(135deg,rgba(220,38,38,.40),rgba(185,28,28,.48)); }
.theme--light .products-details-content .productGroup-wrapper .container.TB { background:linear-gradient(135deg,rgba(239,68,68,.40),rgba(220,38,38,.48)); }
.products-details-content .productGroup-wrapper .container.OMP { background:linear-gradient(135deg,rgba(156,163,175,.35),rgba(209,213,219,.45)); }
.theme--light .products-details-content .productGroup-wrapper .container.OMP { background:linear-gradient(135deg,rgba(209,213,219,.35),rgba(229,231,235,.45)); }
.products-details-content .productGroup-wrapper .container .header { display:flex; align-items:center; gap:.55rem; margin:0 0 .35rem 0; }
.products-details-content .productGroup-wrapper .container .header h4 { font-size:.75rem; letter-spacing:.5px; text-transform:uppercase; font-weight:600; margin:0; }
/* Entfernt erzwungenes großes Gap (calc(12px * 2)) zwischen Produktgruppen-Containern */
.products-details-content .productGroup-wrapper { gap:0 !important; }
.products-details-content .signal-container { margin-bottom:1.25rem; }
.products-details-content .signal-container > .status-heading { display:block; margin:0 0 .65rem 0; }
/* Entfernt ebenfalls Gap in Potential & BK Widget Container-Struktur */
.widget-wrapper.potential-bk .container-wrapper .container { gap:.5rem !important; }
.products-details-content .single-product { background:rgba(0,0,0,.28); border-radius:8px; margin:.35rem 0 .55rem; overflow:hidden; box-shadow:0 1px 2px rgba(0,0,0,.4); }
.theme--light .products-details-content .single-product { background:rgba(255,255,255,.6); box-shadow:0 1px 2px rgba(0,0,0,.15); }
.products-details-content .single-product .headline { width:100%; display:flex; justify-content:space-between; align-items:center; background:transparent; border:none; padding:.55rem .75rem; cursor:pointer; font-size:.72rem; font-weight:600; text-align:left; }
.products-details-content .productGroup-wrapper .container.GS .single-product.open .headline {
    background: linear-gradient(90deg, rgba(255,224,102,0.32) 80%, rgba(255,210,58,0.28) 100%);
    border-bottom: 2px solid #ffe066;
    color: #7a5c00;
}
.theme--light .products-details-content .productGroup-wrapper .container.GS .single-product.open .headline {
    background: linear-gradient(90deg, rgba(255,245,158,0.28) 80%, rgba(255,224,102,0.22) 100%);
    border-bottom: 2px solid #ffe066;
    color: #7a5c00;
}
.products-details-content .productGroup-wrapper .container.OE .single-product.open .headline {
    background: linear-gradient(90deg, rgba(59,130,246,0.18) 80%, rgba(37,99,235,0.16) 100%);
    border-bottom: 2px solid #60a5fa;
    color: #223;
}
.theme--light .products-details-content .productGroup-wrapper .container.OE .single-product.open .headline {
    background: linear-gradient(90deg, rgba(96,165,250,0.16) 80%, rgba(59,130,246,0.13) 100%);
    border-bottom: 2px solid #60a5fa;
    color: #223;
}
.products-details-content .productGroup-wrapper .container.TB .single-product.open .headline {
    background: linear-gradient(90deg, rgba(239,68,68,0.18) 80%, rgba(185,28,28,0.16) 100%);
    border-bottom: 2px solid #ef4444;
    color: #fff;
}
.theme--light .products-details-content .productGroup-wrapper .container.TB .single-product.open .headline {
    background: linear-gradient(90deg, rgba(248,113,113,0.16) 80%, rgba(239,68,68,0.13) 100%);
    border-bottom: 2px solid #ef4444;
    color: #fff;
}
.products-details-content .productGroup-wrapper .container.OMP .single-product.open .headline {
    background: linear-gradient(90deg, rgba(209,213,219,0.22) 80%, rgba(229,231,235,0.18) 100%);
    border-bottom: 2px solid #d1d5db;
    color: #222;
}
.theme--light .products-details-content .productGroup-wrapper .container.OMP .single-product.open .headline {
    background: linear-gradient(90deg, rgba(229,231,235,0.18) 80%, rgba(243,244,246,0.13) 100%);
    border-bottom: 2px solid #d1d5db;
    color: #222;
}
.products-details-content .single-product .headline h4 { font-size:.72rem; margin:0; font-weight:600; }
.products-details-content .single-product .product-info-list { padding:.4rem .6rem .55rem; display:grid; gap:.4rem; }
.products-details-content .product-info .contract-row { background:rgba(0,0,0,.25); padding:.45rem .55rem; border-radius:6px; font-size:.68rem; line-height:1.15rem; }
.theme--light .products-details-content .product-info .contract-row { background:rgba(255,255,255,.55); }
.products-details-content .product-info .contract-main { display:flex; flex-wrap:wrap; gap:.55rem 1rem; align-items:center; }
.products-details-content .product-info p { margin:0; }
.products-details-content .product-info p.pub { font-weight:600; letter-spacing:.3px; }
.products-details-content .product-info .pill { --pill-bg:rgba(255,255,255,.1); --pill-color:#e2e8f0; --pill-border:rgba(255,255,255,.15); display:inline-flex; align-items:center; gap:.35rem; padding:.28rem .65rem; border-radius:999px; font-size: .95rem; font-weight:500; letter-spacing:.35px; line-height:1; background:var(--pill-bg); color:var(--pill-color); border:1px solid var(--pill-border); position:relative; backdrop-filter:saturate(140%) blur(2px); }
.theme--light .products-details-content .product-info .pill { --pill-bg:rgba(0,0,0,.06); --pill-color:#1f2937; --pill-border:rgba(0,0,0,.08); }
.products-details-content .product-info .pill.pill-ist { --pill-bg:linear-gradient(135deg,#0f766e,#115e59); --pill-color:#ecfdf5; --pill-border:rgba(16,185,129,.35); }
.theme--light .products-details-content .product-info .pill.pill-ist { --pill-bg:linear-gradient(135deg,#14b8a6,#0d9488); --pill-color:#f0fdfa; --pill-border:rgba(13,148,136,.4); }
.products-details-content .product-info .pill.pill-date { --pill-bg:linear-gradient(135deg,#334155,#1e293b); --pill-color:#f1f5f9; --pill-border:rgba(148,163,184,.35); }
.theme--light .products-details-content .product-info .pill.pill-date { --pill-bg:linear-gradient(135deg,#e2e8f0,#cbd5e1); --pill-color:#0f172a; --pill-border:rgba(100,116,139,.4); }
.products-details-content .product-info .pill.pill-docs { --pill-bg:linear-gradient(135deg,#4338ca,#3730a3); --pill-color:#eef2ff; --pill-border:rgba(99,102,241,.45); cursor:pointer; }
.theme--light .products-details-content .product-info .pill.pill-docs { --pill-bg:linear-gradient(135deg,#6366f1,#4f46e5); --pill-color:#eef2ff; --pill-border:rgba(99,102,241,.55); }
.products-details-content .product-info .pill.pill-docs.empty { --pill-bg:rgba(255,255,255,.1); --pill-color:#94a3b8; --pill-border:rgba(255,255,255,.15); cursor:default; }
.theme--light .products-details-content .product-info .pill.pill-docs.empty { --pill-bg:rgba(0,0,0,.05); --pill-color:#64748b; --pill-border:rgba(0,0,0,.08); }
.products-details-content .product-info .pill .label { opacity:.85; font-weight:500; letter-spacing:.4px; }
.products-details-content .product-info .pill .value, .products-details-content .product-info .pill .count { font-weight:600; letter-spacing:.4px; }
.products-details-content .product-info .pill.pill-docs .count { background:rgba(255,255,255,.18); padding:.15rem .45rem; border-radius:10px; font-size:.55rem; line-height:1; box-shadow:inset 0 0 0 1px rgba(255,255,255,.15); }
.theme--light .products-details-content .product-info .pill.pill-docs .count { background:rgba(255,255,255,.35); box-shadow:inset 0 0 0 1px rgba(0,0,0,.08); }
.products-details-content .product-info .pill .icon-cal, .products-details-content .product-info .pill .icon-doc { width:12px; height:12px; display:inline-block; filter:drop-shadow(0 0 2px rgba(0,0,0,.35)); opacity:.85; }
.products-details-content .product-info .pill .icon-cal { background:conic-gradient(from 180deg at 50% 50%,#f59e0b,#fcd34d,#f59e0b); mask:radial-gradient(circle at 50% 50%,#000 58%,transparent 61%), linear-gradient(#000,#000); mask-composite:exclude; -webkit-mask-composite:xor; border-radius:3px; }
.products-details-content .product-info .pill .icon-doc { background:linear-gradient(135deg,#e0f2fe,#bae6fd); mask:linear-gradient(#000,#000); border-radius:2px; }
.products-details-content .product-info .pill:hover:not(.empty) { filter:brightness(1.08); }
.products-details-content .product-info .pill:focus-visible { outline:2px solid #60a5fa; outline-offset:2px; }
.products-details-content .product-info .pill.pill-docs[aria-expanded="true"] { box-shadow:0 0 0 2px rgba(255,255,255,.25),0 0 0 1px rgba(255,255,255,.55); }
.theme--light .products-details-content .product-info .pill.pill-docs[aria-expanded="true"] { box-shadow:0 0 0 2px rgba(99,102,241,.35),0 0 0 1px rgba(99,102,241,.6); }
.products-details-content .product-info .no-docs { font-style:italic; opacity:.65; }
.products-details-content .product-info .no-docs { 
    display:inline-flex; 
    align-items:center; 
    gap:.4rem; 
    font-style:italic; 
    opacity:.7; 
    background:rgba(255,255,255,.06); 
    padding:.32rem .6rem; 
    border:1px dashed rgba(255,255,255,.18); 
    border-radius:10px; 
    line-height:1; 
}
.theme--light .products-details-content .product-info .no-docs { 
    background:rgba(0,0,0,.04); 
    border-color:rgba(0,0,0,.15); 
}
.products-details-content .product-info .no-docs .icon-folder { width:14px; height:14px; opacity:.85; filter:drop-shadow(0 0 1px rgba(0,0,0,.35)); }
.theme--light .products-details-content .product-info .no-docs .icon-folder { filter:none; opacity:.8; }
.products-details-content .product-info button.docs-toggle { 
    /* Unified with glass white style */
    background:rgba(255,255,255,.08); 
    color:#f1f5f9; 
    border:1px solid rgba(255,255,255,.28); 
    border-radius:12px; 
    padding:.38rem .7rem; 
    font-size:.55rem; 
    font-weight:600; 
    letter-spacing:.4px; 
    display:inline-flex; 
    align-items:center; 
    gap:.45rem; 
    cursor:pointer; 
    backdrop-filter:blur(8px) saturate(180%); 
    -webkit-backdrop-filter:blur(8px) saturate(180%); 
    box-shadow:0 2px 4px -2px rgba(0,0,0,.45), 0 0 0 1px rgba(255,255,255,.06) inset; 
    transition:background .25s ease,border-color .25s ease,box-shadow .25s ease; 
}
.products-details-content .product-info button.docs-toggle:hover { background:rgba(255,255,255,.16); filter:none; }
.theme--light .products-details-content .product-info button.docs-toggle { background:rgba(255,255,255,.75); color:#1e293b; border:1px solid rgba(255,255,255,.9); box-shadow:0 2px 4px -2px rgba(0,0,0,.15), 0 0 0 1px rgba(255,255,255,.95) inset; }
.theme--light .products-details-content .product-info button.docs-toggle:hover { background:rgba(255,255,255,.9); }
.products-details-content .contract-docs { margin:.35rem 0 0 .1rem; padding:0 .1rem; list-style:none; display:grid; gap:.25rem; }
.products-details-content .contract-docs .contract-doc a.doc-link { display:flex; flex-wrap:wrap; gap:.4rem .6rem; align-items:center; background:rgba(0,0,0,.35); padding:.35rem .5rem; border-radius:6px; font-size:.6rem; text-decoration:none; color:inherit; box-shadow:0 1px 2px -1px rgba(0,0,0,.45); transition:background .25s ease, border-color .25s ease; }
.theme--light .products-details-content .contract-docs .contract-doc a.doc-link { background:rgba(255,255,255,.6); box-shadow:0 1px 2px rgba(0,0,0,.15); }
.products-details-content .contract-docs .doc-cat { font-size:.55rem; font-weight:600; letter-spacing:.5px; padding:.15rem .45rem; border-radius:10px; background:#475569; color:#fff; text-transform:uppercase; }
.theme--light .products-details-content .contract-docs .doc-cat { background:#64748b; }
.reports .report-table { width:100%; border-collapse:separate; border-spacing:0; background:linear-gradient(145deg,#1f2731,#232e3a); border:1px solid #32404f; border-radius:12px; overflow:hidden; font-size:.72rem; box-shadow:0 4px 14px -4px rgba(0,0,0,.45),0 2px 4px -1px rgba(0,0,0,.4); position:relative; }
.theme--light .reports .report-table { background:linear-gradient(145deg,#ffffff,#f2f5f8); border-color:#d8e0e7; box-shadow:0 3px 10px -3px rgba(0,0,0,.12),0 1px 2px rgba(0,0,0,.08); }
.reports .report-table caption { text-align:left; padding:.65rem .9rem; font-weight:600; letter-spacing:.5px; text-transform:uppercase; font-size:.6rem; opacity:.65; }
.reports .report-table thead { background:rgba(255,255,255,.03); }
.theme--light .reports .report-table thead { background:rgba(0,0,0,.04); }
.reports .report-table th, .reports .report-table td { padding:.55rem .55rem; text-align:center; vertical-align:middle; }
.reports .report-table th.metric-head { width:42px; }
.reports .report-table th.total-head { font-size:.62rem; letter-spacing:.3px; }
.reports .report-table tbody tr { transition:background .25s ease; }
.reports .report-table tbody tr:hover { background:rgba(255,255,255,.04); }
.theme--light .reports .report-table tbody tr:hover { background:rgba(0,0,0,.05); }
.reports .report-table .row-label { text-align:left; font-weight:600; font-size:.6rem; letter-spacing:.4px; opacity:.8; width:46px; }
.reports .report-table .icon-chip { display:inline-flex; width:26px; height:26px; border-radius:8px; background:linear-gradient(135deg,#334250,#3a4959); align-items:center; justify-content:center; box-shadow:inset 0 0 0 1px #415161,0 2px 4px -1px rgba(0,0,0,.55); }
.theme--light .reports .report-table .icon-chip { background:linear-gradient(135deg,#e7eef4,#dfe7ee); box-shadow:inset 0 0 0 1px #d0dae3,0 2px 4px -1px rgba(0,0,0,.15); }
.reports .report-table .icon-chip img { width:15px; height:15px; filter:drop-shadow(0 0 2px rgba(0,0,0,.35)); }
.reports .report-table .logo { width:22px; height:22px; filter:drop-shadow(0 0 2px rgba(0,0,0,.35)); }
.reports .report-table td.value-cell { font-variant-numeric:tabular-nums; font-weight:500; }
.reports .report-table td.total { font-weight:600; background:linear-gradient(145deg,#26323f,#2f3c4a); position:relative; }
.theme--light .reports .report-table td.total { background:linear-gradient(145deg,#f1f5f8,#e4eaef); }
.reports .report-table td.total:before { content:""; position:absolute; inset:0; background:repeating-linear-gradient(60deg,rgba(255,255,255,.05) 0 6px,transparent 6px 12px); opacity:.35; pointer-events:none; }
.theme--light .reports .report-table td.total:before { background:repeating-linear-gradient(60deg,rgba(0,0,0,.04) 0 6px,transparent 6px 12px); opacity:.4; }
.reports .report-table .indicator-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}
.reports .indicator-wrapper::before {
    user-select: none;
    pointer-events: none;
}

/* Responsive layout restructure using grid areas */
.flexWrapper.grid { display:grid; gap:.75rem; grid-template-columns:1fr; grid-template-areas:'status' 'potential' 'customer' 'history'; }
.area-status { grid-area: status; }
.area-potential { grid-area: potential; }
.area-customer { grid-area: customer; }
.area-history { grid-area: history; }
@media (min-width: 1024px) {
    .flexWrapper.grid { grid-template-columns: 1fr 1fr; grid-template-areas: 'status potential' 'customer history'; }
}

.reports .indicator-wrapper::after {
    user-select: none;
    pointer-events: none;
}
.indicator-wrapper:has(.reporting-indicator) { margin-bottom:24px; }
.reports .report-table .indicator-wrapper a {
    position: absolute !important;
    min-width: 32px;
    min-height: 32px;
    border-radius: 6px;
    background: transparent;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: inset 0 0 0 1px #3a4a5a, 0 0 0 1px rgba(255,255,255,.04);
    transition: background .3s ease, box-shadow .3s ease, transform .25s ease;
}
.reports .report-table .indicator-wrapper a[data-clickable="true"] { cursor:pointer; }
.reports .report-table .indicator-wrapper a[data-clickable="true"]:hover, .reports .report-table .indicator-wrapper a:hover + .reporting-indicator {
    /* box-shadow:0 0 0 1px #ef7d00,0 0 0 2px rgba(239,125,0,.18); */
    color:#ef7d00;
}
.reporting-indicator {
    display: block;
    font-size: 0.75rem;
    text-align: center;
    margin-top: 64px;
    width: max-content;
    color: #888;
    font-weight: 500;
    letter-spacing: 0.5px;
    transition: color .2s;
}
.reports .report-table .indicator-wrapper a[data-clickable="true"]:focus { outline:2px solid #60a5fa; outline-offset:2px; }
.theme--light .reports .report-table .indicator-wrapper a[data-clickable="true"]:hover { box-shadow:0 0 0 1px #b5c2cc,0 0 0 2px rgba(0,0,0,.05); }
.reports .report-table .indicator-wrapper a.active, .reports .report-table .indicator-wrapper a[data-active="1"] { background:rgba(34,197,94,.10); box-shadow:0 0 0 1px rgba(34,197,94,.45),0 0 0 2px rgba(34,197,94,.18); }
.reports .report-table .indicator-wrapper { transition: transform .25s ease; }
.reports .report-table .indicator-wrapper:not(.presence):has(.active):hover { transform:translateY(-2px); }
.reports .report-table .indicator-wrapper:not(.presence):has(.active):hover { transform:translateY(-2px); }
.theme--light .reports .report-table .indicator-wrapper a { background:transparent; box-shadow:inset 0 0 0 1px #ccd6de; }
.theme--light .reports .report-table .indicator-wrapper a.active, .theme--light .reports .report-table .indicator-wrapper a[data-active="1"] { background:#e6f8ee; box-shadow:0 0 0 1px #b8e8c9,0 0 0 2px rgba(34,197,94,.18); }
.reports .report-table .indicator-wrapper a.red, .reports .report-table .indicator-wrapper a[data-variant="red"] { background:rgba(239,68,68,.12); box-shadow:0 0 0 1px rgba(239,68,68,.45),0 0 0 2px rgba(239,68,68,.18); }
.theme--light .reports .report-table .indicator-wrapper a.red, .theme--light .reports .report-table .indicator-wrapper a[data-variant="red"] { background:#fbeaec; box-shadow:0 0 0 1px #f3c4ca,0 0 0 2px rgba(239,68,68,.22); }
.theme--light .reports .report-table .indicator-wrapper.presence a.active { box-shadow:none; background: none}
.theme--light .reports .report-table .indicator-wrapper.presence a { box-shadow:none; background: none}
/* Inaktive -> dezent rot einfärben */
.reports .collapse-toggle.reports-toggle { gap:.5rem; }
.reports .collapse-toggle.reports-toggle .label { font-size:.7rem; letter-spacing:.5px; }

/* Unify with list-toggle look & feel */
.reports .collapse-toggle.reports-toggle { background:transparent; border:0; padding:.25rem 0 .35rem; box-shadow:none; border-radius:6px; }
.reports .collapse-toggle.reports-toggle:hover { background:rgba(255,255,255,.04); }
.theme--light .reports .collapse-toggle.reports-toggle:hover { background:rgba(0,0,0,.06); }
/* .reports.deactivated .collapse-toggle.reports-toggle { opacity:.65; } */

/* (Legend removed) */

/* Directory collapse transition (height + fade) */

/* If you want to reduce the heavy grayscale effect, soften it */
.reports.deactivated .report-table { filter:grayscale(.15) brightness(.98); }
.reports.deactivated .report-table .indicator-wrapper a { pointer-events:none; }
/* (Heatmap styling removed) */

/* KPI bars removed – numeric values only */
.reports .report-table .omp-label { display:inline-flex; align-items:center; justify-content:center; width:26px; height:26px; border-radius:8px; font-size:.55rem; font-weight:600; letter-spacing:.5px; background:linear-gradient(135deg,#334250,#3a4959); color:#e2e8f0; box-shadow:0 2px 4px -2px rgba(0,0,0,.5); }
.theme--light .reports .report-table .omp-label { background:linear-gradient(145deg,#e7eef4,#dfe7ee); color:#1e293b; box-shadow:0 2px 4px -2px rgba(0,0,0,.15); }

/* Tooltips (pure CSS using data-tip) */
.reports .report-table [data-tip] { position:relative; cursor:help; }
.reports .report-table [data-tip]:hover:after, .reports .report-table [data-tip]:focus-visible:after { content:attr(data-tip); position:absolute; left:50%; bottom:100%; transform:translate(-50%,-6px); background:#0f172a; color:#f1f5f9; padding:.35rem .55rem; border-radius:6px; font-size:.55rem; line-height:1.15; white-space:nowrap; box-shadow:0 4px 16px -4px rgba(0,0,0,.55),0 0 0 1px #1e293b; z-index:20; pointer-events:none; }
.theme--light .reports .report-table [data-tip]:hover:after, .theme--light .reports .report-table [data-tip]:focus-visible:after { background:#1e293b; color:#f8fafc; box-shadow:0 4px 14px -4px rgba(0,0,0,.25),0 0 0 1px #334155; }
.reports .report-table [data-tip]:hover:before, .reports .report-table [data-tip]:focus-visible:before { content:""; position:absolute; left:50%; bottom:100%; transform:translate(-50%,-2px); width:8px; height:8px; background:#0f172a; rotate:45deg; z-index:19; box-shadow:0 0 0 1px #1e293b; }
.theme--light .reports .report-table [data-tip]:hover:before, .theme--light .reports .report-table [data-tip]:focus-visible:before { background:#1e293b; box-shadow:0 0 0 1px #334155; }

/* Minimal indicator variant */
.reports.minimal .report-table .indicator-wrapper a { width:12px; height:12px; border-radius:50%; background:transparent; box-shadow:inset 0 0 0 1px #3a4a5a; }
.reports.minimal .report-table .indicator-wrapper a.active { background:linear-gradient(145deg,#16a34a,#22c55e); box-shadow:0 0 0 1px #16a34a,0 0 0 3px rgba(34,197,94,.35); }
.theme--light .reports.minimal .report-table .indicator-wrapper a { box-shadow:inset 0 0 0 1px #cbd5e1; }
.theme--light .reports.minimal .report-table .indicator-wrapper a.active { background:linear-gradient(145deg,#16a34a,#22c55e); box-shadow:0 0 0 1px #16a34a,0 0 0 3px rgba(34,197,94,.35); }
.theme--light .reports.minimal .report-table .indicator-wrapper.presence a.active { box-shadow:none; background: none}
.theme--light .reports.minimal .report-table .indicator-wrapper.presence a { box-shadow:none; background: none}

/* Compact variant (apply class on .reports or via media) */
.reports.compact .report-table { font-size:.6rem; }
.reports.compact .report-table th,.reports.compact .report-table td { padding:.35rem .3rem; }
.reports.compact .report-table .icon-chip { width:20px; height:20px; }
.reports.compact .report-table .logo { width:16px; height:16px; }

/* Entscheider Dropdown */
.decision-maker-select { margin:.6rem 0 .8rem; display:flex; flex-direction:column; gap:.35rem; }
.decision-maker-select .dm-label { font-size:.65rem; font-weight:600; letter-spacing:.4px; display:inline-block; margin:0; padding:0; text-align:left; }
.decision-maker-select .dm-label .req { display:none; }
.decision-maker-select select { background:rgba(255,255,255,.08); color:#f1f5f9; border:1px solid #334155; padding:.45rem .55rem; border-radius:8px; font-size:.66rem; letter-spacing:.25px; outline:none; transition:border-color .25s ease, background .25s ease; }
.theme--light .decision-maker-select select { background:rgba(255,255,255,.9); color:#1e293b; border-color:#cbd5e1; }
.decision-maker-select select:focus { border-color:#3b82f6; box-shadow:0 0 0 2px rgba(59,130,246,.35); }
.decision-maker-select select.invalid { border-color:#ef4444; box-shadow:0 0 0 2px rgba(239,68,68,.35); }
.decision-maker-select .validation-error { display:none; }
.save-blocking-reasons { list-style:none; margin:.35rem 0 .6rem; padding:0 0 0 1rem; position:relative; }
.save-blocking-reasons li { position:relative; padding-left:.6rem; margin:.15rem 0; font-size:.6rem; line-height:1.25; }
.save-blocking-reasons li::before { content:""; position:absolute; left:6px; top:6px; width:3px; height:3px; background:currentColor; border-radius:50%; opacity:.85; }
.sr-only{ position:absolute !important; width:1px !important; height:1px !important; padding:0 !important; margin:-1px !important; overflow:hidden !important; clip:rect(0,0,0,0) !important; white-space:nowrap !important; border:0 !important; }
/* Google button logo rounding */
button.google { display:inline-flex; align-items:center; gap:.45rem; border-radius:10px; }
button.google .iconWrapper { width:40px; height:40px; border-radius:50%; background:#ffffff; display:flex; align-items:center; justify-content:center; overflow:hidden; box-shadow:0 0 0 1px rgba(0,0,0,.15),0 2px 4px -2px rgba(0,0,0,.35); }
.theme--light button.google .iconWrapper { box-shadow:0 0 0 1px rgba(0,0,0,.08),0 2px 4px -1px rgba(0,0,0,.2); }
button.google .iconWrapper img { width:80%; height:80%; object-fit:contain; border-radius:50%; display:block; }
/* Collapsible user lists */
.listsWrapper { display:flex; flex-direction:column; gap:.35rem; margin-top:.4rem; }
.listsWrapper .lists-compact-header { display:flex; align-items:center; gap:.75rem; background:linear-gradient(135deg,#1e2530,#242e3a); border:1px solid #2f3b48; padding:.35rem .55rem; border-radius:10px; }
.theme--light .listsWrapper .lists-compact-header { background:linear-gradient(135deg,#f1f5f9,#e2e8f0); border-color:#d5dee6; }
.listsWrapper .avatar-expander { all:unset; display:flex; align-items:center; gap:.55rem; cursor:pointer; }
.listsWrapper .avatar-expander:focus-visible { outline:2px solid #0ea5e9; outline-offset:2px; }
.listsWrapper .avatars-row { display:flex; align-items:center; gap:.25rem; }
.listsWrapper .avatars-row :deep(.userAvatar-root) { box-shadow:0 0 0 1px rgba(255,255,255,.1); }
.theme--light .listsWrapper .avatars-row :deep(.userAvatar-root) { box-shadow:0 0 0 1px rgba(0,0,0,.1); }
.listsWrapper .avatar-expander img { width:16px; transition:transform .35s ease; opacity:.75; }
.listsWrapper .avatar-expander img.rotated { transform:rotate(180deg); }
.listsWrapper .lists-tools { margin-left:auto; display:flex; align-items:center; }
.listsWrapper .lists-tools .list-search { font-size:.6rem; padding:.25rem .4rem; border-radius:6px; border:1px solid #2f3b48; background:#1e2530; color:#f1f5f9; width:120px; }
.theme--light .listsWrapper .lists-tools .list-search { border-color:#cbd5e1; background:#fff; color:#1f2937; }
.listsWrapper .avatars-row { display:flex; align-items:center; gap:.2rem; position:relative; }
.listsWrapper .avatars-row .avatar-wrapper { position:relative; display:inline-flex; }
.listsWrapper .avatars-row .avatar-count-badge { position:absolute; bottom:16px; right:6px; min-width:14px; height:14px; padding:0 3px; 
    background:rgba(255,255,255,.18); 
    background:linear-gradient(135deg,rgba(255,255,255,.40),rgba(255,255,255,.15));
    color:#fff; font-size:.75rem; line-height:14px; border-radius:12px; font-weight:600; letter-spacing:.3px; display:flex; align-items:center; justify-content:center; 
    backdrop-filter:blur(6px) saturate(160%); -webkit-backdrop-filter:blur(6px) saturate(160%);
    box-shadow:0 0 0 1px rgba(255,255,255,.28),0 2px 4px -2px rgba(0,0,0,.55),0 0 0 3px rgba(0,0,0,.15); 
    transition:background .25s ease, box-shadow .25s ease; }
.listsWrapper .avatars-row .avatar-count-badge:hover { background:linear-gradient(135deg,rgba(255,255,255,.55),rgba(255,255,255,.25)); }
.theme--light .listsWrapper .avatars-row .avatar-count-badge { background:linear-gradient(135deg,rgba(255,255,255,.85),rgba(255,255,255,.55)); color:#1f2937; box-shadow:0 0 0 1px rgba(0,0,0,.10),0 2px 4px -2px rgba(0,0,0,.25),0 0 0 3px rgba(255,255,255,.55); }
.theme--light .listsWrapper .avatars-row .avatar-count-badge:hover { background:linear-gradient(135deg,rgba(255,255,255,.95),rgba(255,255,255,.72)); }
.listsWrapper .lists-expanded-area { background:linear-gradient(135deg,#202a36,#253140); border:1px solid #324050; border-radius:10px; padding:.5rem .6rem; display:flex; flex-direction:column; gap:.4rem; }
.theme--light .listsWrapper .lists-expanded-area { background:linear-gradient(135deg,#ffffff,#f1f5f9); border-color:#d5dee6; }
.listsWrapper .lists-table-wrapper { padding:.6rem .7rem; }
.listsWrapper .lists-table { width:100%; border-collapse:separate; border-spacing:0 6px; }
.listsWrapper .lists-table thead th { font-size:.55rem; font-weight:600; text-align:left; padding:0 .25rem .25rem; letter-spacing:.5px; opacity:.6; }
.listsWrapper .lists-table .list-row { background:rgba(255,255,255,.04); cursor:pointer; transition:background .2s ease, transform .15s ease; }
.listsWrapper .lists-table .list-row:hover { background:rgba(255,255,255,.08); }
.listsWrapper .lists-table .list-row:focus { outline:2px solid #0ea5e9; outline-offset:2px; }
.theme--light .listsWrapper .lists-table .list-row { background:#f2f6f9; }
.theme--light .listsWrapper .lists-table .list-row:hover { background:#e7edf3; }
.listsWrapper .lists-table td { padding:.4rem .45rem; font-size:.6rem; vertical-align:middle; }
.listsWrapper .lists-table .cell-avatar { width:46px; }
.listsWrapper .single-list-inline { display:flex; align-items:center; }
.listsWrapper .single-list-btn { all:unset; display:inline-flex; align-items:center; gap:.55rem; cursor:pointer; padding:.3rem .55rem; background:linear-gradient(135deg,#1e2530,#242e3a); border:1px solid #2f3b48; border-radius:10px; }
.listsWrapper .single-list-btn .sm-wrapper{padding:0!important;margin:0!important}
.listsWrapper .single-list-btn:hover { background:linear-gradient(135deg,#23303d,#2a3a49); }
.theme--light .listsWrapper .single-list-btn { background:linear-gradient(135deg,#f1f5f9,#e2e8f0); border-color:#d5dee6; }
.theme--light .listsWrapper .single-list-btn:hover { background:linear-gradient(135deg,#e8edf2,#dbe2e9); }
.lists-inline{display:flex;flex-direction: column;gap:.5rem .6rem;min-width:260px}
/* Inline lists: base layout */
/* Base chip (refined smaller) */
.lists-inline .singleList{display:inline-flex;align-items:center;gap:.4rem;padding:.30rem .55rem;border:1px solid #2f3b48;border-radius:9px;cursor:pointer;font-size:.58rem;font-weight:600;letter-spacing:.4px;position:relative;transition:background .25s ease,border-color .25s ease,color .25s ease;line-height:1.1}
/* Mini variant for dropdown lists (explicit class to allow future reuse) */
.lists-inline .singleList.mini{gap:.35rem;padding:.26rem .5rem;font-size:.55rem;border-radius:8px}
/* Active (valid) list uses success semantic (re-using pill mixing approach) */
.lists-inline .singleList:not(.is-invalid){--_c:var(--sellwerk-color-success);background:color-mix(in srgb,var(--_c) 12%,rgba(255,255,255,.04));border-color:color-mix(in srgb,var(--_c) 55%,#2f3b48);color:var(--_c)}
.lists-inline .singleList:not(.is-invalid):hover{background:color-mix(in srgb,var(--_c) 18%,rgba(255,255,255,.07));border-color:color-mix(in srgb,var(--_c) 65%,#2f3b48)}
/* Inactive (invalid/expired) list uses failure semantic */
.lists-inline .singleList.is-invalid{--_c:var(--sellwerk-color-failure);background:color-mix(in srgb,var(--_c) 10%,rgba(255,255,255,.015));border-color:color-mix(in srgb,var(--_c) 55%,#2f3b48);color:var(--_c);cursor:not-allowed;opacity:1;border-style:solid}
.lists-inline .singleList.is-invalid:hover{background:color-mix(in srgb,var(--_c) 14%,rgba(255,255,255,.025))}
/* Name truncation + invalid appearance */
.lists-inline .singleList .name{white-space:nowrap;display:inline-block}
.lists-inline .singleList .name.invalid{text-decoration:line-through;opacity:.7}
/* Validity indicator inherits color; add subtle capsule */
/* Replaced textual badge with validity dot; keep rule placeholder commented */
/* .lists-inline .singleList .valid-indicator { deprecated } */
.lists-inline .singleList .validity-dot{width:10px;height:10px;border-radius:50%;display:inline-block;position:relative;box-shadow:0 0 0 1px color-mix(in srgb,currentColor 55%,transparent),0 0 0 3px rgba(0,0,0,.12);background:color-mix(in srgb,currentColor 35%,transparent)}
.lists-inline .singleList .validity-dot.invalid{background:color-mix(in srgb,currentColor 40%,transparent)}
.lists-inline .singleList .validity-dot .sr-only{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0}
/* Status pill replaces validity-dot (active/inactive) */
.lists-inline .singleList .status-pill{display:inline-flex;align-items:center;gap:.34rem;font-size:.75rem;font-weight:550;letter-spacing:.42px;line-height:1;padding:.26rem .62rem .30rem;border-radius:15px;position:relative;backdrop-filter:blur(6px) saturate(160%);-webkit-backdrop-filter:blur(6px) saturate(160%);background:rgba(255,255,255,.10);box-shadow:0 1px 3px rgba(0,0,0,.40),inset 0 0 0 1px rgba(255,255,255,.20)}
/* Ensure global span rule does not enlarge our tiny label */
.lists-inline .singleList .status-pill .label{font-size:inherit !important;line-height:1;font-weight:inherit;display:inline-block;}
/* Optional responsive scaling (enable if gewünscht):
.lists-inline .singleList .status-pill{font-size:clamp(.40rem,.55vw,.46rem);} */
.lists-inline .singleList.mini .status-pill{font-size:.75rem;padding:.16rem .32rem}
/* Dot removed (no-dot variant) */
.lists-inline .singleList .status-pill .dot{display:none}
.lists-inline .singleList.is-invalid .status-pill{--_c:var(--sellwerk-color-failure)}
.lists-inline .singleList:not(.is-invalid) .status-pill{--_c:var(--sellwerk-color-success)}
.lists-inline .singleList .status-pill.active{color:var(--sellwerk-color-success)}
.lists-inline .singleList .status-pill.inactive{color:var(--sellwerk-color-failure)}
.lists-inline .singleList .status-pill.trailing{margin-left:.4rem}
/* Top-right badge pattern (optional: add class has-status-top on container to activate) */
.lists-inline .singleList.has-status-top{position:relative;padding-right:3.4rem}
.lists-inline .singleList.has-status-top .status-pill.trailing{position:absolute;top:4px;right:6px;margin-left:0}
/* Compact pill variant */
.lists-inline .singleList .status-pill.compact{padding:.14rem .32rem .16rem;font-size:.42rem;gap:.25rem}
.lists-inline .singleList .status-pill.compact .dot{width:5px;height:5px}
/* Hover & focus improvements */
.lists-inline .singleList:focus-visible{outline:2px solid color-mix(in srgb,var(--_c, var(--sellwerk-color-success)) 65%, transparent);outline-offset:2px}
.lists-inline .singleList.is-invalid:focus-visible{outline-color:color-mix(in srgb,var(--sellwerk-color-failure) 65%, transparent)}
.lists-inline .singleList .status-pill{transition:background .2s ease, box-shadow .2s ease}
.lists-inline .singleList:not(.is-invalid):hover .status-pill{background:rgba(255,255,255,.12)}
.lists-inline .singleList.is-invalid:hover .status-pill{background:rgba(255,255,255,.14)}
.theme--light .lists-inline .singleList .status-pill{background:rgba(255,255,255,.55);box-shadow:0 1px 2px rgba(0,0,0,.15),inset 0 0 0 1px rgba(0,0,0,.08)}
.theme--light .lists-inline .singleList:not(.is-invalid):hover .status-pill{background:rgba(255,255,255,.75)}
.theme--light .lists-inline .singleList.is-invalid:hover .status-pill{background:rgba(255,255,255,.80)}
.lists-inline .singleList .status-pill .label{white-space:nowrap}
/* Responsive name truncation: only clamp on very narrow or when chip shrinks */
@media (max-width:820px){
    .lists-inline .singleList .name{max-width:260px;overflow:hidden;text-overflow:ellipsis}
}
@media (max-width:640px){
    .lists-inline .singleList .name{max-width:170px}
}
.lists-inline .lists-empty{font-size:.55rem;opacity:.6;font-style:italic;margin:.15rem 0 .1rem}
.lists-compact-header{background:rgba(255,255,255,.03)!important;border:1px solid rgba(255,255,255,.08)!important;padding:.25rem .5rem!important}
.theme--light .lists-compact-header{background:rgba(0,0,0,.035)!important;border-color:rgba(0,0,0,.08)!important}
/* Dark/light nuance adjustments (overriding base backgrounds) */
.lists-inline .singleList{background:color-mix(in srgb,var(--_c, #94a3b8) 0%,rgba(255,255,255,.03))}
.lists-inline .singleList:not(.is-invalid){background:color-mix(in srgb,var(--_c) 14%,rgba(255,255,255,.04))}
.lists-inline .singleList:not(.is-invalid):hover{background:color-mix(in srgb,var(--_c) 20%,rgba(255,255,255,.07))}
.lists-inline .singleList.is-invalid{background:color-mix(in srgb,var(--_c) 12%,rgba(255,255,255,.02))}
.lists-inline .singleList.is-invalid:hover{background:color-mix(in srgb,var(--_c) 16%,rgba(255,255,255,.03))}
/* Light theme overrides: use white mix instead of dark overlay */
.theme--light .lists-inline .singleList{border-color:color-mix(in srgb,var(--_c, #64748b) 35%,rgba(0,0,0,.08));background:color-mix(in srgb,var(--_c, #64748b) 5%,rgba(0,0,0,.03))}
.theme--light .lists-inline .singleList:not(.is-invalid){background:color-mix(in srgb,var(--_c) 14%,#ffffff)}
.theme--light .lists-inline .singleList:not(.is-invalid):hover{background:color-mix(in srgb,var(--_c) 20%,#ffffff)}
.theme--light .lists-inline .singleList.is-invalid{background:color-mix(in srgb,var(--_c) 12%,#ffffff)}
.theme--light .lists-inline .singleList.is-invalid:hover{background:color-mix(in srgb,var(--_c) 18%,#ffffff)}
.lists-compact-header :deep(.sm-wrapper){padding:0!important;margin:0!important}
/* Remove padding from sm-wrapper inside inline list chips (both dropdown + single) */
:deep(.lists-inline .singleList .sm-wrapper){padding:0!important;margin:0!important}
:deep(.single-list-inline .singleList .sm-wrapper){padding:0!important;margin:0!important}
.lists-heading{margin-top:.4rem;display:flex;align-items:center;justify-content:space-between}
.lists-heading h3{margin:0;font-size:.9rem;font-weight:600;letter-spacing:.5px}
.listsWrapper .single-list-label { font-size:.62rem; font-weight:600; letter-spacing:.45px; color:#f1f5f9; }
.theme--light .listsWrapper .single-list-label { color:#1f2937; }
.listsWrapper .single-list-label.invalid { opacity:.5; text-decoration:line-through; }
.listsWrapper .bp-lists { margin:0; padding:0; list-style:none; display:flex; flex-direction:column; gap:.4rem; }
.listsWrapper .list-line { display:flex; }
.listsWrapper .list-line-inner { flex:1; display:flex; align-items:center; gap:.55rem; padding:.35rem .55rem; background:rgba(255,255,255,.05); border-radius:8px; box-shadow:inset 0 0 0 1px rgba(255,255,255,.06); cursor:pointer; border:none; text-align:left; }
.listsWrapper .list-line-inner:hover { background:rgba(255,255,255,.09); }
.theme--light .listsWrapper .list-line-inner { background:#f1f5f9; box-shadow:inset 0 0 0 1px #d8e0e7; }
.theme--light .listsWrapper .list-line-inner:hover { background:#e8edf2; }
.listsWrapper .list-avatar { width:32px; display:flex; justify-content:center; }
.listsWrapper .avatar-placeholder { width:26px; height:26px; border-radius:50%; background:#334155; display:flex; align-items:center; justify-content:center; font-size:.55rem; color:#fff; opacity:.6; }
.theme--light .listsWrapper .avatar-placeholder { background:#cbd5e1; color:#1f2937; }
.listsWrapper .list-meta { display:flex; flex-direction:column; line-height:1.05; }
.listsWrapper .list-meta .list-name { font-size:.62rem; font-weight:600; letter-spacing:.4px; color:#f1f5f9; }
.theme--light .listsWrapper .list-meta .list-name { color:#1f2937; }
.listsWrapper .list-meta .list-name.invalid { opacity:.45; text-decoration:line-through; }
/* user name Zeilen entfernt (nur Avatar + Listenname sichtbar) */
.listsWrapper .listWrapper { background:linear-gradient(135deg,#1e2530,#242e3a); border:1px solid #2f3b48; border-radius:10px; padding:.25rem .4rem; box-shadow:0 2px 4px -2px rgba(0,0,0,.45); }
.theme--light .listsWrapper .listWrapper { background:linear-gradient(135deg,#f1f5f9,#e2e8f0); border-color:#d5dee6; box-shadow:0 2px 4px -2px rgba(0,0,0,.15); }
.listsWrapper .accordionUserList .list-toggle { all:unset; display:flex; align-items:center; width:100%; cursor:pointer; gap:.55rem; padding:.2rem .2rem; border-radius:8px; position:relative; }
.listsWrapper .accordionUserList .list-toggle:hover { background:rgba(255,255,255,.04); }
.theme--light .listsWrapper .accordionUserList .list-toggle:hover { background:rgba(0,0,0,.06); }
.listsWrapper .accordionUserList .list-toggle:focus-visible { outline:2px solid #0ea5e9; outline-offset:2px; }
.listsWrapper .accordionUserList .users.mini { display:flex; align-items:center; gap:.15rem; }
.listsWrapper .accordionUserList .users.mini .more { font-size:.55rem; background:#334155; color:#e2e8f0; padding:.15rem .3rem; border-radius:12px; }
.theme--light .listsWrapper .accordionUserList .users.mini .more { background:#cbd5e1; color:#1e293b; }

/* === 360° Produkte: Revert / Normalisieren der UI (Icons verkleinern, Dokument-Button & Listendarstellung kompakter) === */
/* Icon-Größe zurückfahren */
.products-details-content .signal-icon { width:20px !important; height:20px !important; max-height:20px !important; }

/* Produkt-Zeile etwas luftiger aber kompakt */
.products-details-content .product-info .contract-row { font-size:.62rem; line-height:1.05rem; padding:.38rem .45rem; }

/* Empty contract hints (no contracts in Kündigung / inaktiv) */
.products-details-content .signal-container .empty-hint { 
    margin:.45rem 0 .8rem; 
    padding:.55rem .75rem; 
    border:1px dashed rgba(255,255,255,.25); 
    border-radius:12px; 
    background:rgba(255,255,255,.04); 
    font-size:.6rem; 
    letter-spacing:.3px; 
    display:inline-flex; 
    align-items:center; 
    gap:.5rem; 
    color:#e2e8f0; 
    position:relative; 
}
.theme--light .products-details-content .signal-container .empty-hint { 
    background:rgba(0,0,0,.035); 
    border-color:rgba(0,0,0,.18); 
    color:#334155; 
}
.products-details-content .signal-container .empty-hint p { 
    margin:0; 
    font-style:italic; 
    opacity:.85; 
}
.products-details-content .signal-container .empty-hint:before { 
    content:""; 
    width:10px; 
    height:10px; 
    border-radius:50%; 
    background:radial-gradient(circle at 30% 30%, #94a3b8, #475569); 
    opacity:.45; 
    box-shadow:0 0 0 1px rgba(255,255,255,.15); 
}
.theme--light .products-details-content .signal-container .empty-hint:before { 
    background:radial-gradient(circle at 30% 30%, #cbd5e1, #94a3b8); 
    box-shadow:0 0 0 1px rgba(0,0,0,.15); 
}
/* Headline wrapper spacing when an arrowWrapper is present */
.headlineWrapper:has(.arrowWrapper) { 
    margin-top:.65rem; 
}
/* Override negative hr margin coming from potentialCustomerDetails.scss */
.singlepage-wrapper:has(.potential-bk) .widget-wrapper.potential-bk .accordion-menu.customer-details .accordion-content-wrapper .customer-details-content .container hr { 
    margin: .5rem 0 !important; 
}

/* Kalender Icon verkleinern & Datum inline ausrichten */
/* Entfernt neue Datum-Icon Inline Variante zugunsten alter Glas-Pill */
.products-details-content .product-info .contract-date-icon { display:none !important; }
/* Runtime & Invoiced Pill (Datum) mit Kalender-Icon im Glas-Style (weiß ähnlich Docs) */
.products-details-content .product-info .pill.pill-date.pill-incoming,
.products-details-content .product-info .pill.pill-date.pill-runtime,
.products-details-content .product-info .pill.pill-date.pill-invoiced { 
    display:inline-flex; align-items:center; gap:.4rem; 
    --pill-bg:rgba(255,255,255,.14); 
    --pill-color:#f8fafc; 
    --pill-border:rgba(255,255,255,.25); 
    background:var(--pill-bg) !important; 
    color:var(--pill-color) !important; 
    border:1px solid var(--pill-border) !important; 
    backdrop-filter:blur(6px) saturate(160%); 
    -webkit-backdrop-filter:blur(6px) saturate(160%); 
    padding:.30rem .65rem; 
}
.theme--light .products-details-content .product-info .pill.pill-date.pill-incoming,
.theme--light .products-details-content .product-info .pill.pill-date.pill-runtime,
.theme--light .products-details-content .product-info .pill.pill-date.pill-invoiced { 
    --pill-bg:rgba(255,255,255,.85); 
    --pill-color:#0f172a; 
    --pill-border:rgba(0,0,0,.12); 
}
.products-details-content .product-info .pill.pill-date.pill-incoming .cal-icon,
.products-details-content .product-info .pill.pill-date.pill-runtime .cal-icon,
.products-details-content .product-info .pill.pill-date.pill-invoiced .cal-icon { width:13px; height:13px; filter:drop-shadow(0 0 2px rgba(0,0,0,.35)); opacity:.9; }
.theme--light .products-details-content .product-info .pill.pill-date.pill-incoming .cal-icon,
.theme--light .products-details-content .product-info .pill.pill-date.pill-runtime .cal-icon,
.theme--light .products-details-content .product-info .pill.pill-date.pill-invoiced .cal-icon { filter:none; opacity:.85; }
.products-details-content .product-info .pill.pill-date.pill-incoming .label,
.products-details-content .product-info .pill.pill-date.pill-runtime .label,
.products-details-content .product-info .pill.pill-date.pill-invoiced .label { font-weight:500; opacity:.85; letter-spacing:.4px; }
.products-details-content .product-info .pill.pill-date.pill-incoming .label,
.products-details-content .product-info .pill.pill-date.pill-cancel .label,
.products-details-content .product-info .pill.pill-date.pill-runtime .label,
.products-details-content .product-info .pill.pill-date.pill-invoiced .label { 
    text-transform:none; 
    font-weight:600; 
    opacity:1; 
    letter-spacing:.25px; 
    font-size:.85rem; 
}

/* Inner date value pill inside runtime/invoiced pill */
.products-details-content .product-info .pill.pill-date.pill-incoming .value-pill,
.products-details-content .product-info .pill.pill-date.pill-runtime .value-pill,
.products-details-content .product-info .pill.pill-date.pill-invoiced .value-pill,
.products-details-content .product-info .pill.pill-date.pill-cancel .value-pill { 
    display:inline-flex; 
    align-items:center; 
    justify-content:center; 
    padding:.18rem .45rem; 
    border-radius:8px; 
    background:rgba(255,255,255,.16); 
    box-shadow:inset 0 0 0 1px rgba(255,255,255,.28); 
    font-size:.75rem; 
    line-height:1; 
    font-weight:600; 
    letter-spacing:.3px; 
    opacity:.9; 
    backdrop-filter:blur(4px) saturate(160%); 
    -webkit-backdrop-filter:blur(4px) saturate(160%); 
}
.theme--light .products-details-content .product-info .pill.pill-date.pill-incoming .value-pill,
.theme--light .products-details-content .product-info .pill.pill-date.pill-runtime .value-pill,
.theme--light .products-details-content .product-info .pill.pill-date.pill-invoiced .value-pill { 
    background:rgba(0,0,0,.06); 
    box-shadow:inset 0 0 0 1px rgba(0,0,0,.12); 
}
/* Cancellation pill reuses runtime pill visual style */
.products-details-content .product-info .pill.pill-date.pill-cancel { 
    --pill-bg:linear-gradient(135deg,#7f1d1d,#991b1b); 
    --pill-color:#fee2e2; 
    --pill-border:rgba(248,113,113,.45); 

    background:var(--pill-bg) !important; 
    color:var(--pill-color) !important; 
    border:1px solid var(--pill-border) !important; 

}
.theme--light .products-details-content .product-info .pill.pill-date.pill-cancel { 
    --pill-bg:linear-gradient(135deg,#fecaca,#fca5a5); 
    --pill-color:#7f1d1d; 
    --pill-border:rgba(185,28,28,.4); 
    background:var(--pill-bg) !important; 
    color:var(--pill-color) !important; 
    border:1px solid var(--pill-border) !important; 
}
/* Cancellation pill reuses runtime pill visual style */

.products-details-content .product-info .pill.pill-date.pill-incoming {
    --pill-bg:linear-gradient(135deg, color-mix(in srgb, var(--btn-success) 80%, #fff 20%), color-mix(in srgb, var(--btn-success) 60%, #fff 40%));
    --pill-color:#e2fee7;
    --pill-border:color-mix(in srgb, var(--btn-success) 40%, #fff 60%);
    background:var(--pill-bg) !important;
    color:var(--pill-color) !important;
    border:1px solid var(--pill-border) !important;
}
.theme--light .products-details-content .product-info .pill.pill-date.pill-incoming {
    --pill-bg:linear-gradient(135deg, color-mix(in srgb, var(--btn-success) 10%, #fff 90%), color-mix(in srgb, var(--btn-success) 30%, #fff 70%));
    --pill-color:var(--btn-success);
    --pill-border:color-mix(in srgb, var(--btn-success) 30%, #fff 70%);
    background:var(--pill-bg) !important;
    color:var(--pill-color) !important;
    border:1px solid var(--pill-border) !important;
}

/* Align cancellation pill icon + label style with runtime */
/* .products-details-content .product-info .pill.pill-date.pill-cancel .cal-icon { width:13px; height:13px; filter:drop-shadow(0 0 2px rgba(0,0,0,.35)); opacity:.9; }
.theme--light .products-details-content .product-info .pill.pill-date.pill-cancel .cal-icon { filter:none; opacity:.85; }
.products-details-content .product-info .pill.pill-date.pill-cancel .label { 
    text-transform:none; 
    font-weight:500; 
    opacity:.8; 
    letter-spacing:.25px; 
    font-size:.58rem; 
} */
/* Order number pill */
.products-details-content .product-info .pill.pill-order { 
    /* Subtler translucent background and full pill shape */
    --pill-bg:rgba(99,102,241,.12); 
    --pill-color:#eef2ff; 
    --pill-border:rgba(129,140,248,.35); 
    display:inline-flex; 
    align-items:center; 
    gap:.40rem; 
    padding:.34rem .70rem; 
    border-radius:100px; 
    backdrop-filter:blur(8px) saturate(180%); 
    -webkit-backdrop-filter:blur(8px) saturate(180%); 
}
.theme--light .products-details-content .product-info .pill.pill-order { 
    --pill-bg:rgba(79,70,229,.08); 
    --pill-color:#1e1b4b; 
    --pill-border:rgba(79,70,229,.28); 
}
.products-details-content .product-info .pill.pill-order .label, 
.products-details-content .product-info .pill.pill-order .value { 
    font-size:.62rem; 
    line-height:1; 
    font-weight:500; 
    letter-spacing:.25px; 
}
.products-details-content .product-info .pill.pill-order .value { font-weight:600; }

/* Contracts: pub + ist + date in einer Zeile (bereits flex-wrap, erzwinge Reihenfolge) */
.products-details-content .product-info .contract-left { display:flex; align-items:center; flex-wrap:wrap; gap:.4rem .55rem; }

/* Two-row contract layout */
.products-details-content .product-info .contract-row.two-rows { padding:.45rem .55rem; display:flex; flex-direction:column; gap:.40rem; }
.products-details-content .product-info .contract-row.two-rows .row { display:flex; align-items:center; width:100%; }
.products-details-content .product-info .contract-row.two-rows .row-top { gap:.65rem; }
.products-details-content .product-info .contract-row.two-rows .row-bottom { gap:.65rem; }
.products-details-content .product-info .contract-row.two-rows .row .left { display:flex; align-items:center; flex-wrap:wrap; gap:.45rem .55rem; }
.products-details-content .product-info .contract-row.two-rows .row .right { margin-left:auto; display:flex; align-items:center; gap:.55rem; margin-top: auto; }

/* Order pill repositioned top-right with subtle glass variant */
.products-details-content .product-info .pill.pill-order.glass-order { 
    --pill-bg:rgba(255,255,255,.12); 
    --pill-color:#f1f5f9; 
    --pill-border:rgba(255,255,255,.28); 
    background:var(--pill-bg) !important; 
    color:var(--pill-color) !important; 
    border:1px solid var(--pill-border) !important; 
    backdrop-filter:blur(6px) saturate(160%); 
    -webkit-backdrop-filter:blur(6px) saturate(160%); 
    padding:.30rem .60rem; 
}
.theme--light .products-details-content .product-info .pill.pill-order.glass-order { 
    --pill-bg:rgba(255,255,255,.82); 
    --pill-color:#1e293b; 
    --pill-border:rgba(0,0,0,.12); 
}
.products-details-content .product-info .pill.pill-order.glass-order .value { font-weight:600; }

/* Adjust docs toggle vertical alignment in new layout */
.products-details-content .product-info .contract-row.two-rows .row-bottom .cluster-docs button.docs-toggle, 
.products-details-content .product-info .contract-row.two-rows .row-bottom .cluster-docs .no-docs { margin-left:auto; margin-top: auto; }

/* Tighten bottom row wrap gaps for small screens */
@media (max-width: 640px) {
  .products-details-content .product-info .contract-row.two-rows .row .left { gap:.35rem .45rem; }
  .products-details-content .product-info .contract-row.two-rows { padding:.40rem .5rem; }
}

/* (Removed duplicate cancel value-pill override; unified above) */

/* Dokumente Bereich komplett rechtsbündig */
.products-details-content .product-info .contract-right { margin-left:auto; display:flex; align-items:center; }
.products-details-content .product-info .contract-main { width:100%; }

/* Docs Toggle wieder Glass Look (leicht) und rechts ausrichten */
.products-details-content .product-info button.docs-toggle { 
    background:rgba(255,255,255,.12) !important; 
    border:1px solid rgba(255,255,255,.28) !important; 
    backdrop-filter:blur(6px) saturate(160%) !important; 
    -webkit-backdrop-filter:blur(6px) saturate(160%) !important; 
    padding:.35rem .65rem !important; 
    font-size:.55rem !important; 
    border-radius:10px !important; 
    gap:.45rem !important; 
    box-shadow:0 2px 4px -2px rgba(0,0,0,.45),0 0 0 1px rgba(255,255,255,.05) inset !important;
}
.theme--light .products-details-content .product-info button.docs-toggle { 
    background:rgba(255,255,255,.75) !important; 
    border:1px solid rgba(255,255,255,.9) !important; 
    box-shadow:0 2px 4px -2px rgba(0,0,0,.15),0 0 0 1px rgba(255,255,255,.95) inset !important; 
}
.products-details-content .product-info button.docs-toggle .count { background:rgba(255,255,255,.18); }
.theme--light .products-details-content .product-info button.docs-toggle .count { background:rgba(0,0,0,.08); }
.products-details-content .product-info button.docs-toggle .caret { display:inline-block !important; }

/* Dokumentliste rechtsbündig: Container erhält Flex-End */
.products-details-content .product-info .contract-row { position:relative; }
/* Dokumentliste wieder voller Breite (Grid) */
.products-details-content .product-info .contract-docs { display:grid; gap:.25rem; margin:.35rem 0 0 .1rem; padding:0 .1rem; }
.products-details-content .product-info .contract-docs .contract-doc a.doc-link { 
    display:flex; flex-wrap:wrap; gap:.4rem .6rem; align-items:center; justify-content:flex-start; 
    background:rgba(0,0,0,.30)!important; padding:.35rem .55rem; border-radius:6px; font-size:.58rem; 
    text-decoration:none; color:inherit; box-shadow:0 1px 2px -1px rgba(0,0,0,.45);
}
.theme--light .products-details-content .product-info .contract-docs .contract-doc a.doc-link { background:rgba(255,255,255,.58)!important; }
.products-details-content .product-info .contract-docs .contract-doc a.doc-link .doc-cat { font-size:.5rem; }
.products-details-content .product-info .contract-docs .contract-doc a.doc-link .doc-name { flex:1 1 auto; min-width:140px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.products-details-content .product-info .contract-docs .contract-doc a.doc-link .doc-name { 
    font-size:.8725rem; 
    font-weight:550; 
    opacity:.88; 
    line-height:1.18; 
    padding:.07rem 0; 
    letter-spacing:.32px; 
}
.products-details-content .product-info .contract-docs .contract-doc a.doc-link .doc-date { margin-left:auto; font-variant-numeric:tabular-nums; opacity:.75; }
/* Volle Breite für Dokument-Zeilen (override evtl. globaler 65ch Begrenzung) */
.products-details-content .product-info .contract-docs, 
.products-details-content .product-info .contract-docs .contract-doc, 
.products-details-content .product-info .contract-docs .contract-doc a.doc-link { max-width:none !important; width:100% !important; }

/* Dokument-Datum als Badge/Pill hervorheben */
.products-details-content .product-info .contract-docs .contract-doc a.doc-link .doc-date { 
    background:rgba(255,255,255,.12); 
    padding:.15rem .45rem; 
    border-radius:10px; 
    font-size:.52rem; 
    line-height:1; 
    font-weight:600; 
    letter-spacing:.4px; 
    opacity:1; 
    box-shadow:inset 0 0 0 1px rgba(255,255,255,.18);
}
.theme--light .products-details-content .product-info .contract-docs .contract-doc a.doc-link .doc-date { 
    background:rgba(0,0,0,.06); 
    box-shadow:inset 0 0 0 1px rgba(0,0,0,.08);
}

/* Docs Toggle kompakter & ohne Glas-Effekt */
.products-details-content .product-info button.docs-toggle {
    background:rgba(0,0,0,.28) !important;
    border:1px solid rgba(255,255,255,.15) !important;
    backdrop-filter:none !important; -webkit-backdrop-filter:none !important;
    padding:.3rem .55rem !important;
    border-radius:6px !important;
    gap:.35rem !important;
    font-size:.6rem !important;
    box-shadow:none !important;
}
.theme--light .products-details-content .product-info button.docs-toggle {
    background:rgba(255,255,255,.65) !important;
    border:1px solid rgba(0,0,0,.1) !important;
}
.products-details-content .product-info button.docs-toggle .label { order:2; }
.products-details-content .product-info button.docs-toggle .count { order:3; background:rgba(0,0,0,.35); padding:.1rem .4rem; border-radius:10px; font-size:.55rem; }
.theme--light .products-details-content .product-info button.docs-toggle .count { background:rgba(0,0,0,.08); }
.products-details-content .product-info button.docs-toggle .caret { display:none; }

/* Dokument-Liste: einzeilig, Name streckt, Datum rechts */
.products-details-content .contract-docs .contract-doc a.doc-link {
    background:rgba(0,0,0,.25) !important;
    font-size:.58rem !important;
    flex-wrap:nowrap !important;
    align-items:center !important;
    gap:.45rem !important;
}
.theme--light .products-details-content .contract-docs .contract-doc a.doc-link { background:rgba(255,255,255,.55) !important; }
.products-details-content .contract-docs .contract-doc a.doc-link .doc-cat { flex:0 0 auto; font-size:.75rem !important; }
.products-details-content .contract-docs .contract-doc a.doc-link .doc-name { flex:1 1 auto; min-width:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.products-details-content .contract-docs .contract-doc a.doc-link .doc-date { margin-left:auto; flex:0 0 auto; font-variant-numeric:tabular-nums; opacity:.8; }

/* Entfernung oberer Label-Verschiebungen (falls durch Wrap entstanden) */
.products-details-content .product-info .contract-main { align-items:center; }

/* Leichter Hover ohne Aufblähen */
.products-details-content .product-info button.docs-toggle:hover { background:rgba(0,0,0,.38) !important; filter:none !important; }
.theme--light .products-details-content .product-info button.docs-toggle:hover { background:rgba(255,255,255,.85) !important; }

/* Falls alte große Ordner-Icons gesetzt wurden, wieder verkleinern */
.products-details-content .product-info button.docs-toggle .icon-folder, 
.products-details-content .product-info .no-docs .icon-folder { width:14px !important; height:14px !important; }

/* Abstand innerhalb .single-product Headline reduzieren */
.products-details-content .single-product .headline { padding:.45rem .6rem !important; }
.products-details-content .single-product .headline h4 { font-size: .9rem !important; }

.listsWrapper .accordionUserList .singleList { font-size:.62rem; font-weight:600; letter-spacing:.4px; flex:1; text-align:left; }
.listsWrapper .accordionUserList .singleList.validPeriod { opacity:.55; text-decoration:line-through; }
.listsWrapper .accordionUserList .arrow { width:14px; height:14px; transition:transform .35s ease; opacity:.75; }
.listsWrapper .accordionUserList .arrow.rotated { transform:rotate(180deg); }
.listsWrapper .usersList { margin-top:.35rem; }
.listsWrapper .usersList .users.detailed { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:.35rem; }
.listsWrapper .usersList .user-line { display:flex; align-items:center; gap:.35rem; background:rgba(255,255,255,.05); padding:.28rem .45rem; border-radius:6px; box-shadow:inset 0 0 0 1px rgba(255,255,255,.05); }
.theme--light .listsWrapper .usersList .user-line { background:#f1f5f9; box-shadow:inset 0 0 0 1px #d8e0e7; }
.listsWrapper .usersList .user-line .username { font-size:.58rem; font-weight:500; letter-spacing:.25px; line-height:1.1; color:#f1f5f9; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:140px; }
.theme--light .listsWrapper .usersList .user-line .username { color:#1f2937; }
/* Override global headline white text for userAvatar texts inside lists */
.listsWrapper .sm-wrapper p.sm-name, .listsWrapper .sm-wrapper p.sm-status { color:#f1f5f9 !important; }
.theme--light .listsWrapper .sm-wrapper p.sm-name, .theme--light .listsWrapper .sm-wrapper p.sm-status { color:#1f2937 !important; }
/* Also adjust any avatar texts inside headline-wrapper specifically when light theme */
.theme--light .headline-wrapper .sm-wrapper p.sm-name, .theme--light .headline-wrapper .sm-wrapper p.sm-status { color:#1f2937; }
/* Deep overrides to penetrate scoped boundaries (userAvatar has its own scope attr) */
:deep(.listsWrapper .sm-wrapper p.sm-name),
:deep(.listsWrapper .sm-wrapper p.sm-status){ color:#e2e8f0; }
.theme--light :deep(.listsWrapper .sm-wrapper p.sm-name),
.theme--light :deep(.listsWrapper .sm-wrapper p.sm-status){ color:#1f2937; }
:deep(.usersList .user-line .sm-wrapper p.sm-name),
:deep(.usersList .user-line .sm-wrapper p.sm-status){ color:#e2e8f0; }
.theme--light :deep(.usersList .user-line .sm-wrapper p.sm-name),
.theme--light :deep(.usersList .user-line .sm-wrapper p.sm-status){ color:#1f2937; }
/* Headline area deep override */
:deep(.headline-wrapper .sm-wrapper p.sm-name),
:deep(.headline-wrapper .sm-wrapper p.sm-status){ color:#f1f5f9; }
.theme--light :deep(.headline-wrapper .sm-wrapper p.sm-name),
.theme--light :deep(.headline-wrapper .sm-wrapper p.sm-status){ color:#1f2937; }

/* Transition */
.fade-slide-enter-active,.fade-slide-leave-active { transition:all .35s cubic-bezier(.4,0,.2,1); overflow:hidden; }
.fade-slide-enter-from,.fade-slide-leave-to { opacity:0; transform:translateY(-4px); max-height:0; }
.fade-slide-enter-to,.fade-slide-leave-from { opacity:1; transform:translateY(0); max-height:400px; }
/* Generic collapse (height + fade) reused for Produkte 360° accordions */
.fade-collapse-enter-active,.fade-collapse-leave-active { transition:opacity .32s ease, max-height .38s cubic-bezier(.4,0,.2,1); overflow:hidden; }
.fade-collapse-enter-from,.fade-collapse-leave-to { opacity:0; max-height:0; }
.fade-collapse-enter-to,.fade-collapse-leave-from { opacity:1; max-height:800px; }
/* Lists toolbar & enhancements */
.listsWrapper .lists-toolbar { display:flex; justify-content:space-between; align-items:center; gap:.75rem; margin-bottom:.35rem; }
.listsWrapper .lists-toolbar .mini-btn { background:#1e293b; color:#f1f5f9; font-size:.55rem; padding:.3rem .55rem; border-radius:6px; border:1px solid #2e3b4b; cursor:pointer; letter-spacing:.3px; }
.listsWrapper .lists-toolbar .mini-btn:hover { background:#253246; }
.theme--light .listsWrapper .lists-toolbar .mini-btn { background:#e2e8f0; color:#1e293b; border-color:#cbd5e1; }
.theme--light .listsWrapper .lists-toolbar .mini-btn:hover { background:#d9e1e9; }
.listsWrapper .lists-toolbar .list-search { background:#0f172a; border:1px solid #2c3a4a; color:#f1f5f9; border-radius:6px; font-size:.6rem; padding:.3rem .45rem; width:120px; }
.listsWrapper .lists-toolbar .list-search:focus { outline:2px solid #0ea5e9; outline-offset:2px; }
.theme--light .listsWrapper .lists-toolbar .list-search { background:#f1f5f9; color:#0f172a; border-color:#cbd5e1; }
.theme--light .reports .report-table .omp-label { background:linear-gradient(135deg,#e7eef4,#dfe7ee); color:#1e293b; box-shadow:inset 0 0 0 1px #d0dae3,0 2px 4px -1px rgba(0,0,0,.15); }

/* Tooltips (pure CSS using data-tip) */
.reports .report-table [data-tip] { position:relative; cursor:help; }
.reports .report-table [data-tip]:hover:after, .reports .report-table [data-tip]:focus-visible:after { content:attr(data-tip); position:absolute; left:50%; bottom:100%; transform:translate(-50%,-6px); background:#0f172a; color:#f1f5f9; padding:.35rem .55rem; border-radius:6px; font-size:.55rem; line-height:1.15; white-space:nowrap; box-shadow:0 4px 16px -4px rgba(0,0,0,.55),0 0 0 1px #1e293b; z-index:20; pointer-events:none; }
.theme--light .reports .report-table [data-tip]:hover:after, .theme--light .reports .report-table [data-tip]:focus-visible:after { background:#1e293b; color:#f8fafc; box-shadow:0 4px 14px -4px rgba(0,0,0,.25),0 0 0 1px #334155; }
.reports .report-table [data-tip]:hover:before, .reports .report-table [data-tip]:focus-visible:before { content:""; position:absolute; left:50%; bottom:100%; transform:translate(-50%,-2px); width:8px; height:8px; background:#0f172a; rotate:45deg; z-index:19; box-shadow:0 0 0 1px #1e293b; }
.theme--light .reports .report-table [data-tip]:hover:before, .theme--light .reports .report-table [data-tip]:focus-visible:before { background:#1e293b; box-shadow:0 0 0 1px #334155; }

/* Minimal indicator variant */
.reports.minimal .report-table .indicator-wrapper a { width:12px; height:12px; border-radius:50%; background:transparent; box-shadow:inset 0 0 0 1px #3a4a5a; }
.reports.minimal .report-table .indicator-wrapper a.active { background:linear-gradient(145deg,#16a34a,#22c55e); box-shadow:0 0 0 1px #16a34a,0 0 0 3px rgba(34,197,94,.35); }
.theme--light .reports.minimal .report-table .indicator-wrapper a { box-shadow:inset 0 0 0 1px #cbd5e1; }
.theme--light .reports.minimal .report-table .indicator-wrapper a.active { background:linear-gradient(145deg,#16a34a,#22c55e); box-shadow:0 0 0 1px #16a34a,0 0 0 3px rgba(34,197,94,.35); }

.reports .report-table .indicator-wrapper.presence a { box-shadow: none; }
.reports .report-table .indicator-wrapper.presence a.active { box-shadow: none; background: none; }


.report-table .indicator-wrapper.presence a { box-shadow: none; border: none;}
/* Compact variant (apply class on .reports or via media) */
.reports.compact .report-table { font-size:.6rem; }
.reports.compact .report-table th,.reports.compact .report-table td { padding:.35rem .3rem; }
.reports.compact .report-table .icon-chip { width:20px; height:20px; }
.reports.compact .report-table .logo { width:16px; height:16px; }
.reports.compact .report-table .kpi-bar .val { font-size:.55rem; }

@media (max-width:880px){
    .reports .report-table { font-size:.58rem; }
    .reports .report-table th,.reports .report-table td { padding:.35rem .3rem; }
    .reports .report-table .icon-chip { width:20px; height:20px; }
    .reports .report-table .logo { width:16px; height:16px; }
    .reports .report-table col.col-all { display:none; }
    .reports .report-table th[title="Gesamt Präsenz"], .reports .report-table td[data-metric="presence"]:nth-of-type(5) { display:none; }
}

@media (max-width:640px){
    .reports .report-table col.col-tb { display:none; }
    .reports .report-table th[title="Das Telefonbuch"], .reports .report-table td[data-metric="revenue"]:nth-of-type(3), .reports .report-table td[data-metric="presence"]:nth-of-type(3) { display:none; }
}

@media (max-width:520px){
    .reports .report-table col.col-gs { display:none; }
    .reports .report-table th[title="Gelbe Seiten"], .reports .report-table td[data-metric="revenue"]:nth-of-type(4), .reports .report-table td[data-metric="presence"]:nth-of-type(4) { display:none; }
    .reports .report-table .kpi-bar .val { font-size:.5rem; }
}

@media (max-width:1100px){
    .reports .report-table { font-size:.62rem; }
    .reports .report-table th,.reports .report-table td { padding:.45rem .4rem; }
    .reports .report-table .icon-chip { width:22px; height:22px; }
    .reports .report-table .logo { width:18px; height:18px; }
}
.sr-only{ position:absolute !important; width:1px !important; height:1px !important; padding:0 !important; margin:-1px !important; overflow:hidden !important; clip:rect(0,0,0,0) !important; white-space:nowrap !important; border:0 !important; }
/* Google button logo rounding */
button.google { display:inline-flex; align-items:center; gap:.45rem; border-radius:10px; }
button.google .iconWrapper { width:40px; height:40px; border-radius:50%; background:#ffffff; display:flex; align-items:center; justify-content:center; overflow:hidden; box-shadow:0 0 0 1px rgba(0,0,0,.15),0 2px 4px -1px rgba(0,0,0,.35); }
.theme--light button.google .iconWrapper { box-shadow:0 0 0 1px rgba(0,0,0,.08),0 2px 4px -1px rgba(0,0,0,.2); }
button.google .iconWrapper img { width:80%; height:80%; object-fit:contain; border-radius:50%; display:block; }
/* Collapsible user lists */
.listsWrapper { display:flex; flex-direction:column; gap:.35rem; margin-top:.4rem; }
.listsWrapper .listWrapper { background:linear-gradient(135deg,#1e2530,#242e3a); border:1px solid #2f3b48; border-radius:10px; padding:.25rem .4rem; box-shadow:0 2px 4px -2px rgba(0,0,0,.45); }
.theme--light .listsWrapper .listWrapper { background:linear-gradient(135deg,#f1f5f9,#e2e8f0); border-color:#d5dee6; box-shadow:0 2px 4px -2px rgba(0,0,0,.15); }
.listsWrapper .accordionUserList .list-toggle { all:unset; display:flex; align-items:center; width:100%; cursor:pointer; gap:.55rem; padding:.2rem .2rem; border-radius:8px; position:relative; }
.listsWrapper .accordionUserList .list-toggle:hover { background:rgba(255,255,255,.04); }
.theme--light .listsWrapper .accordionUserList .list-toggle:hover { background:rgba(0,0,0,.06); }
.listsWrapper .accordionUserList .list-toggle:focus-visible { outline:2px solid #0ea5e9; outline-offset:2px; }
.listsWrapper .accordionUserList .users.mini { display:flex; align-items:center; gap:.15rem; }
.listsWrapper .accordionUserList .users.mini .more { font-size:.55rem; background:#334155; color:#e2e8f0; padding:.15rem .3rem; border-radius:12px; }
.theme--light .listsWrapper .accordionUserList .users.mini .more { background:#cbd5e1; color:#1e293b; }
.listsWrapper .accordionUserList .singleList { font-size:.62rem; font-weight:600; letter-spacing:.4px; flex:1; text-align:left; }
.listsWrapper .accordionUserList .singleList.validPeriod { opacity:.55; text-decoration:line-through; }
.listsWrapper .accordionUserList .arrow { width:14px; height:14px; transition:transform .35s ease; opacity:.75; }
.listsWrapper .accordionUserList .arrow.rotated { transform:rotate(180deg); }
.listsWrapper .usersList { margin-top:.35rem; }
.listsWrapper .usersList .users.detailed { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:.35rem; }
.listsWrapper .usersList .user-line { display:flex; align-items:center; gap:.35rem; background:rgba(255,255,255,.05); padding:.28rem .45rem; border-radius:6px; box-shadow:inset 0 0 0 1px rgba(255,255,255,.05); }
.theme--light .listsWrapper .usersList .user-line { background:#f1f5f9; box-shadow:inset 0 0 0 1px #d8e0e7; }
.listsWrapper .usersList .user-line .username { font-size:.58rem; font-weight:500; letter-spacing:.25px; line-height:1.1; color:#f1f5f9; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:140px; }
.theme--light .listsWrapper .usersList .user-line .username { color:#1f2937; }
/* Override global headline white text for userAvatar texts inside lists */
.listsWrapper .sm-wrapper p.sm-name, .listsWrapper .sm-wrapper p.sm-status { color:#f1f5f9 !important; }
.theme--light .listsWrapper .sm-wrapper p.sm-name, .theme--light .listsWrapper .sm-wrapper p.sm-status { color:#1f2937 !important; }
/* Also adjust any avatar texts inside headline-wrapper specifically when light theme */
.theme--light .headline-wrapper .sm-wrapper p.sm-name, .theme--light .headline-wrapper .sm-wrapper p.sm-status { color:#1f2937; }
/* Deep overrides to penetrate scoped boundaries (userAvatar has its own scope attr) */
:deep(.listsWrapper .sm-wrapper p.sm-name),
:deep(.listsWrapper .sm-wrapper p.sm-status){ color:#e2e8f0; }
.theme--light :deep(.listsWrapper .sm-wrapper p.sm-name),
.theme--light :deep(.listsWrapper .sm-wrapper p.sm-status){ color:#1f2937; }
:deep(.usersList .user-line .sm-wrapper p.sm-name),
:deep(.usersList .user-line .sm-wrapper p.sm-status){ color:#e2e8f0; }
.theme--light :deep(.usersList .user-line .sm-wrapper p.sm-name),
.theme--light :deep(.usersList .user-line .sm-wrapper p.sm-status){ color:#1f2937; }
/* Headline area deep override */
:deep(.headline-wrapper .sm-wrapper p.sm-name),
:deep(.headline-wrapper .sm-wrapper p.sm-status){ color:#f1f5f9; }
.theme--light :deep(.headline-wrapper .sm-wrapper p.sm-name),
.theme--light :deep(.headline-wrapper .sm-wrapper p.sm-status){ color:#1f2937; }

/* Transition */
.fade-slide-enter-active,.fade-slide-leave-active { transition:all .35s cubic-bezier(.4,0,.2,1); overflow:hidden; }
.fade-slide-enter-from,.fade-slide-leave-to { opacity:0; transform:translateY(-4px); max-height:0; }
.fade-slide-enter-to,.fade-slide-leave-from { opacity:1; transform:translateY(0); max-height:400px; }
/* Lists toolbar & enhancements */
.listsWrapper .lists-toolbar { display:flex; justify-content:space-between; align-items:center; gap:.75rem; margin-bottom:.35rem; }
.listsWrapper .lists-toolbar .mini-btn { background:#1e293b; color:#f1f5f9; font-size:.55rem; padding:.3rem .55rem; border-radius:6px; border:1px solid #2e3b4b; cursor:pointer; letter-spacing:.3px; }
.listsWrapper .lists-toolbar .mini-btn:hover { background:#253246; }
.theme--light .listsWrapper .lists-toolbar .mini-btn { background:#e2e8f0; color:#1e293b; border-color:#cbd5e1; }
.theme--light .listsWrapper .lists-toolbar .mini-btn:hover { background:#d9e1e9; }
.listsWrapper .lists-toolbar .list-search { background:#0f172a; border:1px solid #2c3a4a; color:#f1f5f9; border-radius:6px; font-size:.6rem; padding:.3rem .45rem; width:120px; }
.listsWrapper .lists-toolbar .list-search:focus { outline:2px solid #0ea5e9; outline-offset:2px; }
.theme--light .listsWrapper .lists-toolbar .list-search { background:#f1f5f9; color:#0f172a; border-color:#cbd5e1; }
.listsWrapper .listWrapper .count-badge { background:#334155; color:#e2e8f0; font-size:.5rem; padding:.2rem .4rem; border-radius:14px; line-height:1; box-shadow:0 0 0 1px #425469; }
.theme--light .listsWrapper .listWrapper .count-badge { background:#cbd5e1; color:#1e293b; box-shadow:0 0 0 1px #94a3b8; }
/* Contact history avatar layout */
.notes-details-content .note { position:relative; padding:.45rem .55rem .5rem; border-bottom:1px solid rgba(255,255,255,.05); display:grid; grid-template-columns:78px 1fr; column-gap:.75rem; }
.theme--light .notes-details-content .note { border-color:rgba(0,0,0,.07); }
.notes-details-content .note:last-child { border-bottom:none; }
.notes-details-content .note .date { font-size:.55rem; opacity:.65; margin:0; font-weight:500; letter-spacing:.25px; }
.theme--light .notes-details-content .note { border-color:rgba(0,0,0,.07); }
.notes-details-content .note:last-child { border-bottom:none; }
.notes-details-content .note-avatar { flex:0 0 auto; width:28px; height:28px; display:flex; align-items:center; justify-content:center; border-radius:50%; overflow:hidden; position:relative; box-shadow:0 0 0 1px rgba(0,0,0,.35),0 2px 4px -2px rgba(0,0,0,.5); background:#1e2933; }
.theme--light .notes-details-content .note-avatar { background:#e2e8f0; box-shadow:0 0 0 1px rgba(0,0,0,.15),0 1px 2px -1px rgba(0,0,0,.25); }
/* Save blocking reasons list */
.save-blocking-reasons { margin:.4rem 0 .6rem; padding:.45rem .65rem .55rem; list-style:none; background:rgba(220,53,69,.08); border:1px solid rgba(220,53,69,.35); border-radius:8px; display:flex; flex-direction:column; gap:.25rem; font-size:.55rem; line-height:1.15; letter-spacing:.3px; }
.theme--light .save-blocking-reasons { background:rgba(220,53,69,.12); border-color:rgba(220,53,69,.45); }
.save-blocking-reasons li { position:relative; padding-left:14px; }
.save-blocking-reasons li:before { content:""; position:absolute; left:2px; top:6px; width:6px; height:6px; background:linear-gradient(135deg,#dc3545,#f87171); border-radius:50%; box-shadow:0 0 0 1px rgba(220,53,69,.6); }
.theme--light .save-blocking-reasons li:before { box-shadow:0 0 0 1px rgba(220,53,69,.7); }
/* Navigation Layout */
.multiform-navigation.nav-layout { display:flex; justify-content:space-between; align-items:center; gap:1rem; margin-top:.75rem; }
.multiform-navigation.nav-layout .nav-left, .multiform-navigation.nav-layout .nav-right { display:flex; align-items:center; gap:.6rem; }
.multiform-navigation.nav-layout button { min-width:88px; }
.requirements-msg { margin:.4rem 0 .2rem; font-size:.55rem; letter-spacing:.35px; opacity:.75; font-weight:500; }
.theme--light .requirements-msg { opacity:.85; }
/* Disabled Entscheider */
.decision-maker-select.disabled-dm { opacity:.4; pointer-events:none; }
.decision-maker-select.disabled-dm select { cursor:not-allowed; }
.notes-details-content .note-avatar.placeholder { background:#2a3641; color:#cbd5e1; font-size:.55rem; font-weight:600; letter-spacing:.5px; text-transform:uppercase; box-shadow:none; }
.theme--light .notes-details-content .note-avatar.placeholder { background:#cfd8e3; color:#1e293b; }
.notes-details-content .note-avatar .initials { line-height:1; }
.notes-details-content .note .status.information { margin:0; font-size:.58rem; line-height:1.25; }
.notes-details-content .note .status.information .name span:first-child { font-weight:600; }
.notes-details-content .note .comment { font-size:.55rem; line-height:1.25; }
.notes-details-content .note .status.information + .status.information { margin-top:.2rem; }
.notes-details-content { display:flex; flex-direction:column; gap:.1rem; }
.listsWrapper .more-users { margin-top:.4rem; text-align:center; }
.listsWrapper .mini-btn.ghost { background:transparent; border:1px dashed #2e3b4b; color:#cbd5e1; }
.listsWrapper .mini-btn.ghost:hover { background:#1e2532; }
.theme--light .listsWrapper .mini-btn.ghost { border-color:#94a3b8; color:#475569; }
.theme--light .listsWrapper .mini-btn.ghost:hover { background:#e2e8f0; }
/* Sofort Abschluss button styling */
.multiform-navigation .immediate-close { background:#334155; color:#f1f5f9; border:1px solid #475569; padding:.4rem .7rem; font-size:.6rem; border-radius:8px; cursor:pointer; font-weight:600; letter-spacing:.4px; display:inline-flex; align-items:center; justify-content:center; min-width:130px; position:relative; }
.multiform-navigation .immediate-close.disabled { opacity:.45; cursor:not-allowed; }
.multiform-navigation .immediate-close.loading { cursor:progress; }
.multiform-navigation .immediate-close:hover:not(.disabled):not(.loading) { background:#3b485a; }
.theme--light .multiform-navigation .immediate-close { background:#e2e8f0; color:#1e293b; border-color:#cbd5e1; }
.theme--light .multiform-navigation .immediate-close:hover:not(.disabled):not(.loading) { background:#dce3e9; }
.multiform-navigation .immediate-close .loader.mini { width:16px; height:16px; border:3px solid rgba(255,255,255,.35); border-top-color:#38bdf8; border-radius:50%; animation:spin 1s linear infinite; }
.theme--light .multiform-navigation .immediate-close .loader.mini { border:3px solid rgba(0,0,0,.2); border-top-color:#0ea5e9; }
@keyframes spin { to { transform:rotate(360deg); } }
</style>
<style lang="scss">
@use "@/scss/settings/variables.scss" as *;
/* Meta line & pill styles (using SCSS variables) */
.bp-meta-line { display:flex; flex-wrap:wrap; gap:8px; align-items:center; margin:4px 0 10px; font-size:12px; }
.bp-pill { display:inline-flex; align-items:center; gap:4px; padding:3px 10px 4px; border-radius:999px; font-size:11px; font-weight:600; line-height:1.1; letter-spacing:.3px; background:var(--pill-bg,#eef1f4); color:var(--pill-fg,#1f2730); border:1px solid var(--pill-border,#d7dde2); transition:background .2s ease, color .2s ease, border-color .2s ease; }
.bp-pill strong { font-weight:700; }

/* GP */
/* GP neutral (nicht grün) – aufgehellt */
.bp-pill.gp { --pill-bg:#f5f7f9; --pill-border:#e3e8ec; --pill-fg:#1f2730; }

/* Bonität */
.bp-pill.credit { --pill-bg:#edf4ff; --pill-border:#c6ddff; --pill-fg:#094074; }
.bp-pill.credit.cw-ok { --pill-bg:color-mix(in srgb, var(--sellwerk-color-success) 18%, transparent); --pill-border:color-mix(in srgb, var(--sellwerk-color-success) 55%, transparent); --pill-fg:var(--sellwerk-color-success); }
.bp-pill.credit.cw-na { opacity:.6; }

/* Sperren */
.bp-pill.comms.ban-ok { --pill-bg:color-mix(in srgb, var(--sellwerk-color-success) 18%, transparent); --pill-border:color-mix(in srgb, var(--sellwerk-color-success) 55%, transparent); --pill-fg:var(--sellwerk-color-success); }
.bp-pill.comms.ban-warn { --pill-bg:color-mix(in srgb, var(--sellwerk-color-main-accent) 22%, transparent); --pill-border:color-mix(in srgb, var(--sellwerk-color-main-accent) 55%, transparent); --pill-fg:var(--sellwerk-color-main-accent); }
.bp-pill.comms.ban-neutral { --pill-bg:#f5f7f9; --pill-border:#e3e8ec; --pill-fg:#3a4652; }

@media (prefers-color-scheme: dark){
    .bp-pill { --pill-bg:#1f272e; --pill-border:#38434d; --pill-fg:#dfe6ef; }
    /* Dark mode neutrale GP Pill (leicht aufgehellt relativ zum Hintergrund) */
    .bp-pill.gp { --pill-bg:#263039; --pill-border:#3a4753; --pill-fg:#d0d7dd; }
    .bp-pill.credit { --pill-bg:#0d2b46; --pill-border:#1d476d; --pill-fg:#b8dfff; }
    .bp-pill.credit.cw-ok { --pill-bg:color-mix(in srgb, var(--sellwerk-color-success) 20%, transparent); --pill-border:color-mix(in srgb, var(--sellwerk-color-success) 60%, transparent); --pill-fg:var(--sellwerk-color-success); }
    .bp-pill.comms.ban-ok { --pill-bg:color-mix(in srgb, var(--sellwerk-color-success) 20%, transparent); --pill-border:color-mix(in srgb, var(--sellwerk-color-success) 60%, transparent); --pill-fg:var(--sellwerk-color-success); }
    .bp-pill.comms.ban-warn { --pill-bg:color-mix(in srgb, var(--sellwerk-color-main-accent) 26%, transparent); --pill-border:color-mix(in srgb, var(--sellwerk-color-main-accent) 62%, transparent); --pill-fg:var(--sellwerk-color-main-accent); }
    .bp-pill.comms.ban-neutral { --pill-bg:#2a343d; --pill-border:#3d4a55; --pill-fg:#d0d7dd; }
}
</style>